import type { OfferId } from './model';

export const localeOptions = [
  { code: 'en-US', label: 'English (US)', paddleLocale: 'en', direction: 'ltr' },
  { code: 'en-GB', label: 'English (UK)', paddleLocale: 'en', direction: 'ltr' },
  { code: 'zh-CN', label: '简体中文', paddleLocale: 'zh-Hans', direction: 'ltr' },
  { code: 'zh-TW', label: '繁體中文', paddleLocale: 'zh-TW', direction: 'ltr' },
  { code: 'ja-JP', label: '日本語', paddleLocale: 'ja', direction: 'ltr' },
  { code: 'ko-KR', label: '한국어', paddleLocale: 'ko', direction: 'ltr' },
  { code: 'es-ES', label: 'Español', paddleLocale: 'es', direction: 'ltr' },
  { code: 'pt-PT', label: 'Português', paddleLocale: 'pt', direction: 'ltr' },
  { code: 'pt-BR', label: 'Português (Brasil)', paddleLocale: 'pt-BR', direction: 'ltr' },
  { code: 'fr-FR', label: 'Français', paddleLocale: 'fr', direction: 'ltr' },
  { code: 'de-DE', label: 'Deutsch', paddleLocale: 'de', direction: 'ltr' },
  { code: 'it-IT', label: 'Italiano', paddleLocale: 'it', direction: 'ltr' },
  { code: 'nl-NL', label: 'Nederlands', paddleLocale: 'nl', direction: 'ltr' },
  { code: 'pl-PL', label: 'Polski', paddleLocale: 'pl', direction: 'ltr' },
  { code: 'da-DK', label: 'Dansk', paddleLocale: 'da', direction: 'ltr' },
  { code: 'sv-SE', label: 'Svenska', paddleLocale: 'sv', direction: 'ltr' },
  { code: 'nb-NO', label: 'Norsk', paddleLocale: 'no', direction: 'ltr' },
  { code: 'tr-TR', label: 'Türkçe', paddleLocale: 'tr', direction: 'ltr' },
  { code: 'ru-RU', label: 'Русский', paddleLocale: 'ru', direction: 'ltr' },
  { code: 'ar', label: 'العربية', paddleLocale: 'ar', direction: 'rtl' },
] as const;

export type SupportedLocale = (typeof localeOptions)[number]['code'];
export type PaddleCheckoutLocale = (typeof localeOptions)[number]['paddleLocale'];

export const reviewedLocaleCodes = [
  'en-US',
  'en-GB',
  'zh-CN',
] as const satisfies readonly SupportedLocale[];
export type ReviewedLocale = (typeof reviewedLocaleCodes)[number];
const reviewedLocaleSet = new Set<SupportedLocale>(reviewedLocaleCodes);
export const publicLocaleOptions = localeOptions.filter((option) =>
  reviewedLocaleSet.has(option.code),
);

export type OfferCopy = {
  name: string;
  description: string;
  features: readonly string[];
  cta: string;
};

export type CheckoutCopy = {
  metaTitle: string;
  metaDescription: string;
  languageLabel: string;
  heroEyebrow: string;
  heroTitle: string;
  heroDescription: string;
  sandboxBadge: string;
  offersLabel: string;
  loadingPrices: string;
  localizedPriceReady: string;
  chooseInPhotoKeeper: string;
  priceUnavailable: string;
  selectedBadge: string;
  oneTimeLicense: string;
  licenseUpgrade: string;
  oneTimeQuantity: string;
  signInToUpgrade: string;
  upgradeEyebrow: string;
  upgradeTitle: string;
  upgradeDescription: string;
  trustTitle: string;
  trustDescription: string;
  disclaimer: string;
  paymentCompleted: string;
  checkoutError: string;
  priceLoadError: string;
  upgradeRequiresSignIn: string;
  purchaseRequiresSignIn: string;
  openingCheckout: string;
  loadingTitle: string;
  loadingDescription: string;
  welcomeEyebrow: string;
  welcomeTitle: string;
  welcomeDescription: string;
  welcomeClose: string;
  fatalTitle: string;
  invalidLink: string;
  checkoutGone: string;
  pricingUnavailable: string;
  offers: Record<OfferId, OfferCopy>;
};

const enUS: CheckoutCopy = {
  metaTitle: 'PhotoKeeper One-time Licenses',
  metaDescription: 'PhotoKeeper one-time licenses and secure Paddle checkout.',
  languageLabel: 'Language',
  heroEyebrow: 'PhotoKeeper desktop app',
  heroTitle: 'Keep your photos close. Pay once.',
  heroDescription:
    'Back up media from your own authenticated iCloud Photos account to storage you control.',
  sandboxBadge: 'Sandbox checkout · No real payment',
  offersLabel: 'PhotoKeeper one-time licenses',
  loadingPrices: 'Loading country-localized prices…',
  localizedPriceReady: 'The displayed total comes directly from Paddle for your location.',
  chooseInPhotoKeeper: 'Choose a license in PhotoKeeper to create a permanently bound checkout.',
  priceUnavailable: 'Price unavailable',
  selectedBadge: 'Selected license',
  oneTimeLicense: 'One-time License',
  licenseUpgrade: 'License upgrade',
  oneTimeQuantity: 'One-time payment · Quantity 1',
  signInToUpgrade: 'Sign in to upgrade',
  upgradeEyebrow: 'Already have Personal?',
  upgradeTitle: 'Upgrade the license you already own',
  upgradeDescription:
    'Upgrade eligibility is verified by the PhotoKeeper licensing service before a transaction is created.',
  trustTitle: 'Local-first by design',
  trustDescription:
    'PhotoKeeper stores backups on your chosen local storage. Your Apple Account password and authentication tokens are never sent to Paddle or the PhotoKeeper licensing service.',
  disclaimer:
    'PhotoKeeper is independently developed and is not affiliated with, sponsored by, or endorsed by Apple Inc. Paddle is the Merchant of Record for purchases; the PhotoKeeper developer remains responsible for the product, support, and PhotoKeeper-controlled data. Review the Privacy, EULA, Refund, and Support policies bundled in PhotoKeeper before purchasing.',
  paymentCompleted: 'Payment completed. PhotoKeeper is securely verifying your license.',
  checkoutError: 'Checkout could not complete. Review the payment details and try again.',
  priceLoadError:
    'Paddle could not load localized prices. Please reload this page and try again.',
  upgradeRequiresSignIn:
    'Sign in to PhotoKeeper with the Apple Account bound to your active Personal license to upgrade.',
  purchaseRequiresSignIn:
    'For permanent account binding, start the purchase inside PhotoKeeper after authenticating your Apple Account.',
  openingCheckout: 'Opening secure Paddle checkout…',
  loadingTitle: 'Preparing PhotoKeeper pricing',
  loadingDescription: 'Loading secure checkout configuration and localized totals…',
  welcomeEyebrow: 'Payment received',
  welcomeTitle: 'Return to PhotoKeeper',
  welcomeDescription:
    'PhotoKeeper will unlock or upgrade only after its licensing service verifies the signed transaction.completed webhook. This usually takes a few seconds.',
  welcomeClose: 'You may safely close this browser tab.',
  fatalTitle: 'Checkout unavailable',
  invalidLink: 'This checkout link is invalid. Return to PhotoKeeper and try again.',
  checkoutGone:
    'This checkout is no longer available. Return to PhotoKeeper to start or resume it safely.',
  pricingUnavailable:
    'Pricing is temporarily unavailable. Return to PhotoKeeper and try again shortly.',
  offers: {
    personal: {
      name: 'PhotoKeeper Personal',
      description: 'A one-time PhotoKeeper License for one permanently bound Apple Account.',
      features: [
        'Back up media from your authenticated iCloud Photos account',
        'Local-first storage on your computer',
        'One permanently bound Apple Account',
        'Offline access: up to 24 hours in the first 15 days or during a refund; otherwise up to 30 days',
      ],
      cta: 'Buy Personal',
    },
    family: {
      name: 'PhotoKeeper Family · 3 Apple Accounts',
      description:
        'A one-time PhotoKeeper License for up to three permanently bound Apple Accounts.',
      features: [
        'Back up media from authenticated iCloud Photos accounts',
        'Local-first storage on your computer',
        'Up to three permanently bound Apple Accounts',
        'Offline access: up to 24 hours in the first 15 days or during a refund; otherwise up to 30 days',
      ],
      cta: 'Buy Family',
    },
    upgrade: {
      name: 'PhotoKeeper Personal to Family Upgrade · 3 Apple Accounts',
      description:
        'Converts an existing active Personal License to Family and increases its permanent account limit from one to three. This is not a standalone License.',
      features: [
        'Requires an active Personal License',
        'Keeps the existing license and permanent binding',
        'Raises the account limit from one to three',
        'Offline access: up to 24 hours in the first 15 days or during a refund; otherwise up to 30 days',
      ],
      cta: 'Upgrade to Family',
    },
  },
};

const enGB: CheckoutCopy = {
  ...enUS,
  metaTitle: 'PhotoKeeper One-time Licences',
  metaDescription: 'PhotoKeeper one-time licences and secure Paddle checkout.',
  offersLabel: 'PhotoKeeper one-time licences',
  loadingPrices: 'Loading country-localised prices…',
  localizedPriceReady: 'The displayed total comes directly from Paddle for your location.',
  priceLoadError:
    'Paddle could not load localised prices. Please reload this page and try again.',
  disclaimer:
    'PhotoKeeper is independently developed and is not affiliated with, sponsored by, or endorsed by Apple Inc. Paddle is the Merchant of Record for purchases; the PhotoKeeper developer remains responsible for the product, support, and PhotoKeeper-controlled data. Review the Privacy, EULA, Refund, and Support policies bundled in PhotoKeeper before purchasing.',
  oneTimeLicense: 'One-time Licence',
  licenseUpgrade: 'Licence upgrade',
  upgradeTitle: 'Upgrade the licence you already own',
  offers: {
    personal: {
      ...enUS.offers.personal,
      description: 'A one-time PhotoKeeper Licence for one permanently bound Apple Account.',
    },
    family: {
      ...enUS.offers.family,
      description:
        'A one-time PhotoKeeper Licence for up to three permanently bound Apple Accounts.',
    },
    upgrade: {
      ...enUS.offers.upgrade,
      description:
        'Converts an existing active Personal Licence to Family and increases its permanent account limit from one to three. This is not a standalone licence.',
      features: ['Requires an active Personal Licence', ...enUS.offers.upgrade.features.slice(1)],
    },
  },
};

const zhCN: CheckoutCopy = {
  metaTitle: 'PhotoKeeper 一次性 License',
  metaDescription: 'PhotoKeeper 一次性 License 与安全的 Paddle 结账页面。',
  languageLabel: '语言',
  heroEyebrow: 'PhotoKeeper Windows 版',
  heroTitle: '让照片留在身边，一次付费。',
  heroDescription: '将您已认证的 iCloud 照片账户中的媒体备份到由您掌控的存储空间。',
  sandboxBadge: 'Sandbox 测试结账 · 不会产生真实付款',
  offersLabel: 'PhotoKeeper 一次性 License',
  loadingPrices: '正在加载您所在国家或地区的本地化价格…',
  localizedPriceReady: '显示的总额由 Paddle 根据您的所在地直接提供。',
  chooseInPhotoKeeper: '请在 PhotoKeeper 中选择许可，以创建永久绑定的结账。',
  priceUnavailable: '价格暂不可用',
  selectedBadge: '已选择的许可',
  oneTimeLicense: '一次性 License',
  licenseUpgrade: '许可升级',
  oneTimeQuantity: '一次性付款 · 数量 1',
  signInToUpgrade: '登录后升级',
  upgradeEyebrow: '已经拥有个人版？',
  upgradeTitle: '升级您已有的许可',
  upgradeDescription: 'PhotoKeeper 许可服务会在创建交易前验证您的升级资格。',
  trustTitle: '本地优先设计',
  trustDescription:
    'PhotoKeeper 将备份保存在您选择的本地存储中。Apple 账户密码和认证令牌不会发送给 Paddle 或 PhotoKeeper 授权服务。',
  disclaimer:
    'PhotoKeeper 为独立开发产品，与 Apple Inc. 无隶属、赞助或认可关系。Paddle 是购买交易的 Merchant of Record；PhotoKeeper 开发者仍负责产品、支持及由 PhotoKeeper 控制的数据。购买前请阅读 PhotoKeeper 内置的隐私政策、EULA、退款政策与支持政策。',
  paymentCompleted: '付款已完成。PhotoKeeper 正在安全验证您的许可。',
  checkoutError: '结账未能完成。请检查付款信息后重试。',
  priceLoadError: 'Paddle 无法加载本地化价格，请刷新此页面后重试。',
  upgradeRequiresSignIn: '请使用绑定到有效个人版许可的 Apple 账户登录 PhotoKeeper 后升级。',
  purchaseRequiresSignIn:
    '为确保账户永久绑定，请先在 PhotoKeeper 中认证您的 Apple 账户，然后发起购买。',
  openingCheckout: '正在打开安全的 Paddle 结账…',
  loadingTitle: '正在准备 PhotoKeeper 价格',
  loadingDescription: '正在加载安全的结账配置和本地化总额…',
  welcomeEyebrow: '已收到付款',
  welcomeTitle: '返回 PhotoKeeper',
  welcomeDescription:
    '只有在许可服务验证签名的 transaction.completed Webhook 后，PhotoKeeper 才会解锁或升级。通常只需几秒钟。',
  welcomeClose: '您现在可以安全关闭此浏览器标签页。',
  fatalTitle: '结账暂不可用',
  invalidLink: '此结账链接无效。请返回 PhotoKeeper 后重试。',
  checkoutGone: '此结账已不可用。请返回 PhotoKeeper，安全地重新开始或继续操作。',
  pricingUnavailable: '价格服务暂时不可用。请稍后返回 PhotoKeeper 重试。',
  offers: {
    personal: {
      name: 'PhotoKeeper 个人版',
      description: 'PhotoKeeper 一次性 License，永久绑定 1 个 Apple 账户。',
      features: [
        '备份已认证 iCloud 照片账户中的媒体',
        '在您的电脑上采用本地优先存储',
        '永久绑定 1 个 Apple 账户',
        '购买或升级后 15 天内、退款期间可离线 24 小时；其他情况最长 30 天',
      ],
      cta: '购买个人版',
    },
    family: {
      name: 'PhotoKeeper 家庭版 · 3 个 Apple 账户',
      description: 'PhotoKeeper 一次性 License，最多永久绑定 3 个 Apple 账户。',
      features: [
        '备份已认证 iCloud 照片账户中的媒体',
        '在您的电脑上采用本地优先存储',
        '最多永久绑定 3 个 Apple 账户',
        '购买或升级后 15 天内、退款期间可离线 24 小时；其他情况最长 30 天',
      ],
      cta: '购买家庭版',
    },
    upgrade: {
      name: 'PhotoKeeper 个人版升级至家庭版 · 3 个 Apple 账户',
      description:
        '将现有有效的个人版 License 升级为家庭版，永久账户上限由 1 个提高到 3 个。此产品不能单独作为 License 使用。',
      features: [
        '需要有效的个人版 License',
        '保留现有许可和永久绑定',
        '账户上限由 1 个提高到 3 个',
        '购买或升级后 15 天内、退款期间可离线 24 小时；其他情况最长 30 天',
      ],
      cta: '升级至家庭版',
    },
  },
};

const zhTW: CheckoutCopy = {
  metaTitle: 'PhotoKeeper 終身授權',
  metaDescription: 'PhotoKeeper 終身授權與安全的 Paddle 結帳頁面。',
  languageLabel: '語言',
  heroEyebrow: 'PhotoKeeper Windows 版',
  heroTitle: '讓相片留在身邊，一次付費。',
  heroDescription: '將您已驗證的 iCloud 相片帳號中的媒體備份到由您掌控的儲存空間。',
  sandboxBadge: 'Sandbox 測試結帳 · 不會產生真實付款',
  offersLabel: 'PhotoKeeper 終身授權',
  loadingPrices: '正在載入您所在國家或地區的本地化價格…',
  localizedPriceReady: '顯示的總額由 Paddle 根據您的所在地直接提供。',
  chooseInPhotoKeeper: '請在 PhotoKeeper 中選擇授權，以建立永久綁定的結帳。',
  priceUnavailable: '價格暫不可用',
  selectedBadge: '已在 PhotoKeeper 中選擇',
  oneTimeLicense: '終身授權',
  licenseUpgrade: '授權升級',
  oneTimeQuantity: '一次性付款 · 數量 1',
  signInToUpgrade: '登入後升級',
  upgradeEyebrow: '已經擁有個人版？',
  upgradeTitle: '升級您已有的授權',
  upgradeDescription: 'PhotoKeeper 授權服務會在建立交易前驗證您的升級資格。',
  trustTitle: '本機優先設計',
  trustDescription:
    'PhotoKeeper 將備份儲存在您選擇的本機儲存空間。Paddle 只處理付款，不會收到您的 iCloud 憑證或驗證權杖。',
  disclaimer: 'PhotoKeeper 為獨立開發產品，與 Apple Inc. 無隸屬、贊助或認可關係。',
  paymentCompleted: '付款已完成。PhotoKeeper 正在安全地驗證您的授權。',
  checkoutError: '結帳未能完成。請檢查付款資料後再試一次。',
  priceLoadError: 'Paddle 無法載入本地化價格。結帳已停用，請返回 PhotoKeeper 後重試。',
  upgradeRequiresSignIn: '請使用綁定至有效個人版授權的 iCloud 帳號登入 PhotoKeeper 後升級。',
  purchaseRequiresSignIn:
    '為確保帳號永久綁定，請先在 PhotoKeeper 中驗證您的 iCloud 帳號，再開始購買。',
  openingCheckout: '正在開啟安全的 Paddle 結帳…',
  loadingTitle: '正在準備 PhotoKeeper 價格',
  loadingDescription: '正在載入安全的 Sandbox 設定與本地化總額…',
  welcomeEyebrow: '已收到付款',
  welcomeTitle: '返回 PhotoKeeper',
  welcomeDescription:
    '只有在授權服務驗證已簽署的 transaction.completed Webhook 後，PhotoKeeper 才會解鎖或升級。通常只需幾秒鐘。',
  welcomeClose: '您現在可以安全地關閉此瀏覽器分頁。',
  fatalTitle: '結帳暫不可用',
  invalidLink: '此結帳連結無效。請返回 PhotoKeeper 後重試。',
  checkoutGone: '此結帳已不可用。請返回 PhotoKeeper，安全地重新開始或繼續操作。',
  pricingUnavailable: '價格服務暫時不可用。請稍後返回 PhotoKeeper 重試。',
  offers: {
    personal: {
      name: 'PhotoKeeper 個人版終身授權',
      description: 'PhotoKeeper 一次性終身授權，永久綁定 1 個 iCloud 帳號。',
      features: [
        '備份已驗證 iCloud 相片帳號中的媒體',
        '在您的 Windows 電腦上採用本機優先儲存',
        '永久綁定 1 個 iCloud 帳號',
        '一次性付款',
      ],
      cta: '購買個人版終身授權',
    },
    family: {
      name: 'PhotoKeeper 家庭版終身授權',
      description: 'PhotoKeeper 一次性終身授權，最多永久綁定 3 個 iCloud 帳號。',
      features: [
        '備份已驗證 iCloud 相片帳號中的媒體',
        '在您的 Windows 電腦上採用本機優先儲存',
        '最多永久綁定 3 個 iCloud 帳號',
        '一次性付款',
      ],
      cta: '購買家庭版終身授權',
    },
    upgrade: {
      name: 'PhotoKeeper 個人版升級至家庭版',
      description:
        '將現有有效的個人版終身授權升級為家庭版，永久帳號上限由 1 個提高至 3 個。此產品不能單獨作為授權使用。',
      features: [
        '需要有效的個人版終身授權',
        '保留現有授權與永久綁定',
        '帳號上限由 1 個提高至 3 個',
        '一次性付款',
      ],
      cta: '升級至家庭版',
    },
  },
};

const jaJP: CheckoutCopy = {
  metaTitle: 'PhotoKeeper 永久ライセンス',
  metaDescription: 'PhotoKeeper 永久ライセンスと安全な Paddle 決済。',
  languageLabel: '言語',
  heroEyebrow: 'Windows 用 PhotoKeeper',
  heroTitle: '写真をいつも手元に。お支払いは一度だけ。',
  heroDescription:
    '認証済みのご自身の iCloud写真アカウントから、管理できる保存先へメディアをバックアップします。',
  sandboxBadge: 'Sandbox テスト決済 · 実際の請求はありません',
  offersLabel: 'PhotoKeeper 永久ライセンス',
  loadingPrices: 'お住まいの国・地域向け価格を読み込んでいます…',
  localizedPriceReady: '表示される合計金額は、お客様の所在地に基づき Paddle から直接提供されます。',
  chooseInPhotoKeeper: 'PhotoKeeper でライセンスを選択し、永久紐付け用の決済を作成してください。',
  priceUnavailable: '価格を取得できません',
  selectedBadge: 'PhotoKeeper で選択済み',
  oneTimeLicense: '永久ライセンス',
  licenseUpgrade: 'ライセンスのアップグレード',
  oneTimeQuantity: '一回払い · 数量 1',
  signInToUpgrade: 'サインインしてアップグレード',
  upgradeEyebrow: 'Personal をお持ちですか？',
  upgradeTitle: 'お持ちのライセンスをアップグレード',
  upgradeDescription:
    '取引を作成する前に、PhotoKeeper ライセンスサービスがアップグレード資格を確認します。',
  trustTitle: 'ローカルファースト設計',
  trustDescription:
    'PhotoKeeper は選択したローカルストレージにバックアップを保存します。Paddle は決済のみを処理し、iCloud の認証情報や認証トークンを受け取ることはありません。',
  disclaimer:
    'PhotoKeeper は独立して開発されており、Apple Inc. との提携、後援、承認関係はありません。',
  paymentCompleted: 'お支払いが完了しました。PhotoKeeper がライセンスを安全に確認しています。',
  checkoutError: '決済を完了できませんでした。お支払い情報を確認して、もう一度お試しください。',
  priceLoadError:
    'Paddle から地域別価格を読み込めませんでした。決済は無効です。PhotoKeeper に戻って再試行してください。',
  upgradeRequiresSignIn:
    '有効な Personal ライセンスに紐付いた iCloud アカウントで PhotoKeeper にサインインしてからアップグレードしてください。',
  purchaseRequiresSignIn:
    'アカウントを永久に紐付けるため、PhotoKeeper 内で iCloud アカウントを認証してから購入を開始してください。',
  openingCheckout: '安全な Paddle 決済を開いています…',
  loadingTitle: 'PhotoKeeper の価格を準備しています',
  loadingDescription: '安全な Sandbox 設定と地域別合計金額を読み込んでいます…',
  welcomeEyebrow: 'お支払いを受け付けました',
  welcomeTitle: 'PhotoKeeper に戻る',
  welcomeDescription:
    'ライセンスサービスが署名済みの transaction.completed Webhook を確認した後にのみ、PhotoKeeper がロック解除またはアップグレードされます。通常は数秒で完了します。',
  welcomeClose: 'このブラウザータブは安全に閉じることができます。',
  fatalTitle: '決済を利用できません',
  invalidLink: 'この決済リンクは無効です。PhotoKeeper に戻ってもう一度お試しください。',
  checkoutGone:
    'この決済は利用できなくなりました。PhotoKeeper に戻り、安全に開始または再開してください。',
  pricingUnavailable:
    '価格情報を一時的に利用できません。しばらくしてから PhotoKeeper に戻って再試行してください。',
  offers: {
    personal: {
      name: 'PhotoKeeper Personal 永久ライセンス',
      description:
        'PhotoKeeper の一回払い永久ライセンスです。1つの iCloud アカウントに永久に紐付きます。',
      features: [
        '認証済み iCloud写真アカウントからメディアをバックアップ',
        'お使いのコンピューター上のローカルファーストストレージ',
        '1つの iCloud アカウントに永久紐付け',
        '一回払い',
      ],
      cta: 'Personal 永久ライセンスを購入',
    },
    family: {
      name: 'PhotoKeeper Family 永久ライセンス',
      description:
        'PhotoKeeper の一回払い永久ライセンスです。最大3つの iCloud アカウントに永久に紐付きます。',
      features: [
        '認証済み iCloud写真アカウントからメディアをバックアップ',
        'お使いのコンピューター上のローカルファーストストレージ',
        '最大3つの iCloud アカウントに永久紐付け',
        '一回払い',
      ],
      cta: 'Family 永久ライセンスを購入',
    },
    upgrade: {
      name: 'PhotoKeeper Personal から Family へのアップグレード',
      description:
        '有効な Personal 永久ライセンスを Family に変更し、永久アカウント上限を1から3へ増やします。単体ではライセンスとして使用できません。',
      features: [
        '有効な Personal 永久ライセンスが必要',
        '既存のライセンスと永久紐付けを維持',
        'アカウント上限を1から3へ拡張',
        '一回払い',
      ],
      cta: 'Family にアップグレード',
    },
  },
};

const koKR: CheckoutCopy = {
  metaTitle: 'PhotoKeeper 평생 라이선스',
  metaDescription: 'PhotoKeeper 평생 라이선스 및 안전한 Paddle 결제.',
  languageLabel: '언어',
  heroEyebrow: 'Windows용 PhotoKeeper',
  heroTitle: '사진을 가까이. 결제는 한 번만.',
  heroDescription: '인증된 본인의 iCloud 사진 계정에서 직접 관리하는 저장소로 미디어를 백업하세요.',
  sandboxBadge: 'Sandbox 테스트 결제 · 실제 결제 없음',
  offersLabel: 'PhotoKeeper 평생 라이선스',
  loadingPrices: '국가별 현지 가격을 불러오는 중…',
  localizedPriceReady: '표시된 총액은 현재 위치를 기준으로 Paddle에서 직접 제공합니다.',
  chooseInPhotoKeeper: 'PhotoKeeper에서 라이선스를 선택해 영구 연결 결제를 생성하세요.',
  priceUnavailable: '가격을 사용할 수 없음',
  selectedBadge: 'PhotoKeeper에서 선택됨',
  oneTimeLicense: '평생 라이선스',
  licenseUpgrade: '라이선스 업그레이드',
  oneTimeQuantity: '일회성 결제 · 수량 1',
  signInToUpgrade: '로그인하여 업그레이드',
  upgradeEyebrow: 'Personal을 사용 중이신가요?',
  upgradeTitle: '기존 라이선스 업그레이드',
  upgradeDescription: '거래 생성 전에 PhotoKeeper 라이선스 서비스가 업그레이드 자격을 확인합니다.',
  trustTitle: '로컬 우선 설계',
  trustDescription:
    'PhotoKeeper는 선택한 로컬 저장소에 백업을 저장합니다. Paddle은 결제만 처리하며 iCloud 자격 증명이나 인증 토큰을 받지 않습니다.',
  disclaimer:
    'PhotoKeeper는 독립적으로 개발되었으며 Apple Inc.와 제휴, 후원 또는 보증 관계가 없습니다.',
  paymentCompleted: '결제가 완료되었습니다. PhotoKeeper에서 라이선스를 안전하게 확인하고 있습니다.',
  checkoutError: '결제를 완료하지 못했습니다. 결제 정보를 확인하고 다시 시도하세요.',
  priceLoadError:
    'Paddle에서 현지 가격을 불러오지 못했습니다. 결제가 비활성화되었습니다. PhotoKeeper로 돌아가 다시 시도하세요.',
  upgradeRequiresSignIn:
    '활성 Personal 라이선스에 연결된 iCloud 계정으로 PhotoKeeper에 로그인한 후 업그레이드하세요.',
  purchaseRequiresSignIn:
    '계정을 영구 연결하려면 PhotoKeeper에서 iCloud 계정을 인증한 후 구매를 시작하세요.',
  openingCheckout: '안전한 Paddle 결제를 여는 중…',
  loadingTitle: 'PhotoKeeper 가격 준비 중',
  loadingDescription: '안전한 Sandbox 설정과 현지 총액을 불러오는 중…',
  welcomeEyebrow: '결제 접수 완료',
  welcomeTitle: 'PhotoKeeper로 돌아가기',
  welcomeDescription:
    '라이선스 서비스가 서명된 transaction.completed Webhook을 확인한 후에만 PhotoKeeper가 잠금 해제되거나 업그레이드됩니다. 일반적으로 몇 초 정도 걸립니다.',
  welcomeClose: '이 브라우저 탭을 안전하게 닫아도 됩니다.',
  fatalTitle: '결제를 사용할 수 없음',
  invalidLink: '이 결제 링크는 유효하지 않습니다. PhotoKeeper로 돌아가 다시 시도하세요.',
  checkoutGone:
    '이 결제는 더 이상 사용할 수 없습니다. PhotoKeeper로 돌아가 안전하게 시작하거나 재개하세요.',
  pricingUnavailable:
    '가격 정보를 일시적으로 사용할 수 없습니다. 잠시 후 PhotoKeeper로 돌아가 다시 시도하세요.',
  offers: {
    personal: {
      name: 'PhotoKeeper Personal 평생 라이선스',
      description: 'PhotoKeeper 일회성 평생 라이선스입니다. iCloud 계정 1개에 영구 연결됩니다.',
      features: [
        '인증된 iCloud 사진 계정의 미디어 백업',
        '컴퓨터의 로컬 우선 저장소',
        'iCloud 계정 1개 영구 연결',
        '일회성 결제',
      ],
      cta: 'Personal 평생 라이선스 구매',
    },
    family: {
      name: 'PhotoKeeper Family 평생 라이선스',
      description:
        'PhotoKeeper 일회성 평생 라이선스입니다. 최대 3개의 iCloud 계정에 영구 연결됩니다.',
      features: [
        '인증된 iCloud 사진 계정의 미디어 백업',
        '컴퓨터의 로컬 우선 저장소',
        '최대 3개의 iCloud 계정 영구 연결',
        '일회성 결제',
      ],
      cta: 'Family 평생 라이선스 구매',
    },
    upgrade: {
      name: 'PhotoKeeper Personal에서 Family로 업그레이드',
      description:
        '활성 Personal 평생 라이선스를 Family로 전환하고 영구 계정 한도를 1개에서 3개로 늘립니다. 단독 라이선스가 아닙니다.',
      features: [
        '활성 Personal 평생 라이선스 필요',
        '기존 라이선스와 영구 연결 유지',
        '계정 한도를 1개에서 3개로 확대',
        '일회성 결제',
      ],
      cta: 'Family로 업그레이드',
    },
  },
};

const esES: CheckoutCopy = {
  metaTitle: 'Licencias de por vida de PhotoKeeper',
  metaDescription: 'Licencias de por vida de PhotoKeeper y pago seguro con Paddle.',
  languageLabel: 'Idioma',
  heroEyebrow: 'PhotoKeeper para Windows',
  heroTitle: 'Tus fotos siempre cerca. Paga una sola vez.',
  heroDescription:
    'Haz copias de tus archivos desde tu propia cuenta autenticada de Fotos en iCloud al almacenamiento que tú controlas.',
  sandboxBadge: 'Pago de prueba Sandbox · Sin cobros reales',
  offersLabel: 'Licencias de por vida de PhotoKeeper',
  loadingPrices: 'Cargando precios locales para tu país…',
  localizedPriceReady: 'El total mostrado procede directamente de Paddle según tu ubicación.',
  chooseInPhotoKeeper:
    'Elige una licencia en PhotoKeeper para crear un pago vinculado de forma permanente.',
  priceUnavailable: 'Precio no disponible',
  selectedBadge: 'Seleccionado en PhotoKeeper',
  oneTimeLicense: 'Licencia de por vida',
  licenseUpgrade: 'Actualización de licencia',
  oneTimeQuantity: 'Pago único · Cantidad 1',
  signInToUpgrade: 'Inicia sesión para actualizar',
  upgradeEyebrow: '¿Ya tienes Personal?',
  upgradeTitle: 'Actualiza la licencia que ya tienes',
  upgradeDescription:
    'El servicio de licencias de PhotoKeeper verifica si puedes actualizar antes de crear una transacción.',
  trustTitle: 'Diseñado para guardar todo en local',
  trustDescription:
    'PhotoKeeper guarda las copias en el almacenamiento local que elijas. Paddle procesa el pago, pero nunca recibe tus credenciales ni tokens de autenticación de iCloud.',
  disclaimer:
    'PhotoKeeper se desarrolla de forma independiente y no está afiliado, patrocinado ni respaldado por Apple Inc.',
  paymentCompleted: 'Pago completado. PhotoKeeper está verificando tu licencia de forma segura.',
  checkoutError: 'No se pudo completar el pago. Revisa los datos de pago e inténtalo de nuevo.',
  priceLoadError:
    'Paddle no pudo cargar los precios locales. El pago está desactivado; vuelve a PhotoKeeper e inténtalo de nuevo.',
  upgradeRequiresSignIn:
    'Inicia sesión en PhotoKeeper con la cuenta de iCloud vinculada a tu licencia Personal activa para actualizarla.',
  purchaseRequiresSignIn:
    'Para vincular la cuenta de forma permanente, inicia la compra en PhotoKeeper después de autenticar tu cuenta de iCloud.',
  openingCheckout: 'Abriendo el pago seguro de Paddle…',
  loadingTitle: 'Preparando los precios de PhotoKeeper',
  loadingDescription: 'Cargando la configuración segura de Sandbox y los totales locales…',
  welcomeEyebrow: 'Pago recibido',
  welcomeTitle: 'Vuelve a PhotoKeeper',
  welcomeDescription:
    'PhotoKeeper solo se desbloqueará o actualizará después de que el servicio de licencias verifique el webhook transaction.completed firmado. Normalmente tarda unos segundos.',
  welcomeClose: 'Ya puedes cerrar esta pestaña del navegador de forma segura.',
  fatalTitle: 'Pago no disponible',
  invalidLink: 'Este enlace de pago no es válido. Vuelve a PhotoKeeper e inténtalo de nuevo.',
  checkoutGone:
    'Este pago ya no está disponible. Vuelve a PhotoKeeper para iniciarlo o reanudarlo de forma segura.',
  pricingUnavailable:
    'Los precios no están disponibles temporalmente. Vuelve a PhotoKeeper e inténtalo de nuevo en unos instantes.',
  offers: {
    personal: {
      name: 'PhotoKeeper Personal de por vida',
      description:
        'Licencia de pago único y de por vida para PhotoKeeper. Vincula permanentemente una cuenta de iCloud.',
      features: [
        'Copia de archivos de tu cuenta autenticada de Fotos en iCloud',
        'Almacenamiento local en tu ordenador',
        'Una cuenta de iCloud vinculada permanentemente',
        'Pago único',
      ],
      cta: 'Comprar Personal de por vida',
    },
    family: {
      name: 'PhotoKeeper Family de por vida',
      description:
        'Licencia de pago único y de por vida para PhotoKeeper. Vincula permanentemente hasta tres cuentas de iCloud.',
      features: [
        'Copia de archivos de cuentas autenticadas de Fotos en iCloud',
        'Almacenamiento local en tu ordenador',
        'Hasta tres cuentas de iCloud vinculadas permanentemente',
        'Pago único',
      ],
      cta: 'Comprar Family de por vida',
    },
    upgrade: {
      name: 'Actualización de PhotoKeeper Personal a Family',
      description:
        'Convierte una licencia Personal activa de por vida en Family y aumenta el límite permanente de una a tres cuentas. No es una licencia independiente.',
      features: [
        'Requiere una licencia Personal activa de por vida',
        'Conserva la licencia y la vinculación permanente existentes',
        'Aumenta el límite de una a tres cuentas',
        'Pago único',
      ],
      cta: 'Actualizar a Family',
    },
  },
};

const ptPT: CheckoutCopy = {
  metaTitle: 'Licenças vitalícias do PhotoKeeper',
  metaDescription: 'Licenças vitalícias do PhotoKeeper e pagamento seguro com a Paddle.',
  languageLabel: 'Idioma',
  heroEyebrow: 'PhotoKeeper para Windows',
  heroTitle: 'Mantenha as suas fotografias por perto. Pague uma vez.',
  heroDescription:
    'Faça cópias dos ficheiros da sua própria conta autenticada de Fotografias em iCloud para um armazenamento que controla.',
  sandboxBadge: 'Pagamento de teste Sandbox · Sem cobrança real',
  offersLabel: 'Licenças vitalícias do PhotoKeeper',
  loadingPrices: 'A carregar preços locais para o seu país…',
  localizedPriceReady: 'O total apresentado vem diretamente da Paddle com base na sua localização.',
  chooseInPhotoKeeper:
    'Escolha uma licença no PhotoKeeper para criar um pagamento com associação permanente.',
  priceUnavailable: 'Preço indisponível',
  selectedBadge: 'Selecionado no PhotoKeeper',
  oneTimeLicense: 'Licença vitalícia',
  licenseUpgrade: 'Atualização de licença',
  oneTimeQuantity: 'Pagamento único · Quantidade 1',
  signInToUpgrade: 'Iniciar sessão para atualizar',
  upgradeEyebrow: 'Já tem o Personal?',
  upgradeTitle: 'Atualize a licença que já possui',
  upgradeDescription:
    'O serviço de licenças do PhotoKeeper verifica a elegibilidade antes de criar uma transação.',
  trustTitle: 'Concebido para armazenamento local',
  trustDescription:
    'O PhotoKeeper guarda as cópias no armazenamento local que escolher. A Paddle processa o pagamento, mas nunca recebe as suas credenciais ou tokens de autenticação do iCloud.',
  disclaimer:
    'O PhotoKeeper é desenvolvido de forma independente e não é afiliado, patrocinado nem aprovado pela Apple Inc.',
  paymentCompleted:
    'Pagamento concluído. O PhotoKeeper está a verificar a sua licença em segurança.',
  checkoutError: 'Não foi possível concluir o pagamento. Reveja os dados e tente novamente.',
  priceLoadError:
    'A Paddle não conseguiu carregar os preços locais. O pagamento foi desativado; volte ao PhotoKeeper e tente novamente.',
  upgradeRequiresSignIn:
    'Inicie sessão no PhotoKeeper com a conta iCloud associada à sua licença Personal ativa para atualizar.',
  purchaseRequiresSignIn:
    'Para associar a conta permanentemente, inicie a compra no PhotoKeeper depois de autenticar a sua conta iCloud.',
  openingCheckout: 'A abrir o pagamento seguro da Paddle…',
  loadingTitle: 'A preparar os preços do PhotoKeeper',
  loadingDescription: 'A carregar a configuração segura do Sandbox e os totais locais…',
  welcomeEyebrow: 'Pagamento recebido',
  welcomeTitle: 'Volte ao PhotoKeeper',
  welcomeDescription:
    'O PhotoKeeper só será desbloqueado ou atualizado depois de o serviço de licenças verificar o webhook transaction.completed assinado. Normalmente demora alguns segundos.',
  welcomeClose: 'Pode fechar este separador do navegador em segurança.',
  fatalTitle: 'Pagamento indisponível',
  invalidLink: 'Este link de pagamento é inválido. Volte ao PhotoKeeper e tente novamente.',
  checkoutGone:
    'Este pagamento já não está disponível. Volte ao PhotoKeeper para o iniciar ou retomar em segurança.',
  pricingUnavailable:
    'Os preços estão temporariamente indisponíveis. Volte ao PhotoKeeper e tente novamente em breve.',
  offers: {
    personal: {
      name: 'PhotoKeeper Personal Vitalício',
      description:
        'Uma licença vitalícia de pagamento único para o PhotoKeeper. Associa permanentemente uma conta iCloud.',
      features: [
        'Cópia dos ficheiros da sua conta autenticada de Fotografias em iCloud',
        'Armazenamento local no seu computador',
        'Uma conta iCloud associada permanentemente',
        'Pagamento único',
      ],
      cta: 'Comprar Personal Vitalício',
    },
    family: {
      name: 'PhotoKeeper Family Vitalício',
      description:
        'Uma licença vitalícia de pagamento único para o PhotoKeeper. Associa permanentemente até três contas iCloud.',
      features: [
        'Cópia dos ficheiros de contas autenticadas de Fotografias em iCloud',
        'Armazenamento local no seu computador',
        'Até três contas iCloud associadas permanentemente',
        'Pagamento único',
      ],
      cta: 'Comprar Family Vitalício',
    },
    upgrade: {
      name: 'Atualização do PhotoKeeper Personal para Family',
      description:
        'Converte uma licença Personal Vitalícia ativa em Family e aumenta o limite permanente de uma para três contas. Não é uma licença autónoma.',
      features: [
        'Requer uma licença Personal Vitalícia ativa',
        'Mantém a licença e a associação permanente existentes',
        'Aumenta o limite de uma para três contas',
        'Pagamento único',
      ],
      cta: 'Atualizar para Family',
    },
  },
};

const ptBR: CheckoutCopy = {
  ...ptPT,
  metaTitle: 'Licenças vitalícias do PhotoKeeper',
  metaDescription: 'Licenças vitalícias do PhotoKeeper e checkout seguro com a Paddle.',
  heroTitle: 'Mantenha suas fotos por perto. Pague uma vez.',
  heroDescription:
    'Faça backup da mídia da sua própria conta autenticada do Fotos do iCloud para um armazenamento que você controla.',
  loadingPrices: 'Carregando preços locais para o seu país…',
  localizedPriceReady: 'O total exibido vem diretamente da Paddle com base na sua localização.',
  chooseInPhotoKeeper:
    'Escolha uma licença no PhotoKeeper para criar um checkout com vínculo permanente.',
  selectedBadge: 'Selecionado no PhotoKeeper',
  signInToUpgrade: 'Entrar para fazer upgrade',
  upgradeEyebrow: 'Já tem o Personal?',
  upgradeTitle: 'Faça upgrade da licença que você já possui',
  upgradeDescription:
    'O serviço de licenciamento do PhotoKeeper verifica a elegibilidade antes de criar uma transação.',
  trustTitle: 'Feito para armazenamento local',
  trustDescription:
    'O PhotoKeeper salva os backups no armazenamento local que você escolher. A Paddle processa o pagamento, mas nunca recebe suas credenciais ou tokens de autenticação do iCloud.',
  paymentCompleted:
    'Pagamento concluído. O PhotoKeeper está verificando sua licença com segurança.',
  checkoutError: 'Não foi possível concluir o pagamento. Confira os dados e tente novamente.',
  priceLoadError:
    'A Paddle não conseguiu carregar os preços locais. O checkout foi desativado; volte ao PhotoKeeper e tente novamente.',
  upgradeRequiresSignIn:
    'Entre no PhotoKeeper com a conta do iCloud vinculada à sua licença Personal ativa para fazer o upgrade.',
  purchaseRequiresSignIn:
    'Para vincular a conta permanentemente, inicie a compra no PhotoKeeper depois de autenticar sua conta do iCloud.',
  openingCheckout: 'Abrindo o checkout seguro da Paddle…',
  loadingTitle: 'Preparando os preços do PhotoKeeper',
  loadingDescription: 'Carregando a configuração segura do Sandbox e os totais locais…',
  welcomeEyebrow: 'Pagamento recebido',
  welcomeTitle: 'Volte ao PhotoKeeper',
  welcomeDescription:
    'O PhotoKeeper só será desbloqueado ou atualizado depois que o serviço de licenciamento verificar o webhook transaction.completed assinado. Isso normalmente leva alguns segundos.',
  welcomeClose: 'Você já pode fechar esta aba do navegador com segurança.',
  invalidLink: 'Este link de checkout é inválido. Volte ao PhotoKeeper e tente novamente.',
  checkoutGone:
    'Este checkout não está mais disponível. Volte ao PhotoKeeper para iniciar ou retomar com segurança.',
  pricingUnavailable:
    'Os preços estão temporariamente indisponíveis. Volte ao PhotoKeeper e tente novamente em instantes.',
  offers: {
    personal: {
      name: 'PhotoKeeper Personal Vitalício',
      description:
        'Uma licença vitalícia de pagamento único para o PhotoKeeper. Vincula permanentemente uma conta do iCloud.',
      features: [
        'Backup da mídia da sua conta autenticada do Fotos do iCloud',
        'Armazenamento local no seu computador',
        'Uma conta do iCloud vinculada permanentemente',
        'Pagamento único',
      ],
      cta: 'Comprar Personal Vitalício',
    },
    family: {
      name: 'PhotoKeeper Family Vitalício',
      description:
        'Uma licença vitalícia de pagamento único para o PhotoKeeper. Vincula permanentemente até três contas do iCloud.',
      features: [
        'Backup da mídia de contas autenticadas do Fotos do iCloud',
        'Armazenamento local no seu computador',
        'Até três contas do iCloud vinculadas permanentemente',
        'Pagamento único',
      ],
      cta: 'Comprar Family Vitalício',
    },
    upgrade: {
      name: 'Upgrade do PhotoKeeper Personal para Family',
      description:
        'Converte uma licença Personal Vitalícia ativa em Family e aumenta o limite permanente de uma para três contas. Não é uma licença independente.',
      features: [
        'Requer uma licença Personal Vitalícia ativa',
        'Mantém a licença e o vínculo permanente existentes',
        'Aumenta o limite de uma para três contas',
        'Pagamento único',
      ],
      cta: 'Fazer upgrade para Family',
    },
  },
};

const frFR: CheckoutCopy = {
  metaTitle: 'Licences à vie PhotoKeeper',
  metaDescription: 'Licences à vie PhotoKeeper et paiement sécurisé avec Paddle.',
  languageLabel: 'Langue',
  heroEyebrow: 'PhotoKeeper pour Windows',
  heroTitle: 'Gardez vos photos près de vous. Payez une seule fois.',
  heroDescription:
    'Sauvegardez les contenus de votre propre compte Photos iCloud authentifié vers un stockage que vous contrôlez.',
  sandboxBadge: 'Paiement test Sandbox · Aucun paiement réel',
  offersLabel: 'Licences à vie PhotoKeeper',
  loadingPrices: 'Chargement des prix adaptés à votre pays…',
  localizedPriceReady: 'Le total affiché provient directement de Paddle selon votre localisation.',
  chooseInPhotoKeeper:
    'Choisissez une licence dans PhotoKeeper pour créer un paiement associé de façon permanente.',
  priceUnavailable: 'Prix indisponible',
  selectedBadge: 'Sélectionné dans PhotoKeeper',
  oneTimeLicense: 'Licence à vie',
  licenseUpgrade: 'Mise à niveau de licence',
  oneTimeQuantity: 'Paiement unique · Quantité 1',
  signInToUpgrade: 'Se connecter pour mettre à niveau',
  upgradeEyebrow: 'Vous avez déjà Personal ?',
  upgradeTitle: 'Mettez à niveau votre licence actuelle',
  upgradeDescription:
    'Le service de licences PhotoKeeper vérifie votre éligibilité avant de créer une transaction.',
  trustTitle: 'Conçu pour le stockage local',
  trustDescription:
    'PhotoKeeper enregistre les sauvegardes sur le stockage local de votre choix. Paddle traite le paiement, mais ne reçoit jamais vos identifiants ni vos jetons d’authentification iCloud.',
  disclaimer:
    'PhotoKeeper est développé de façon indépendante et n’est ni affilié, ni sponsorisé, ni approuvé par Apple Inc.',
  paymentCompleted: 'Paiement terminé. PhotoKeeper vérifie votre licence de manière sécurisée.',
  checkoutError:
    'Le paiement n’a pas pu aboutir. Vérifiez les informations de paiement et réessayez.',
  priceLoadError:
    'Paddle n’a pas pu charger les prix locaux. Le paiement est désactivé ; retournez dans PhotoKeeper et réessayez.',
  upgradeRequiresSignIn:
    'Connectez-vous à PhotoKeeper avec le compte iCloud associé à votre licence Personal active pour la mettre à niveau.',
  purchaseRequiresSignIn:
    'Pour associer définitivement le compte, lancez l’achat dans PhotoKeeper après avoir authentifié votre compte iCloud.',
  openingCheckout: 'Ouverture du paiement sécurisé Paddle…',
  loadingTitle: 'Préparation des prix PhotoKeeper',
  loadingDescription: 'Chargement de la configuration Sandbox sécurisée et des totaux locaux…',
  welcomeEyebrow: 'Paiement reçu',
  welcomeTitle: 'Retournez dans PhotoKeeper',
  welcomeDescription:
    'PhotoKeeper ne sera déverrouillé ou mis à niveau qu’après vérification du webhook transaction.completed signé par le service de licences. Cela prend généralement quelques secondes.',
  welcomeClose: 'Vous pouvez fermer cet onglet en toute sécurité.',
  fatalTitle: 'Paiement indisponible',
  invalidLink: 'Ce lien de paiement n’est pas valide. Retournez dans PhotoKeeper et réessayez.',
  checkoutGone:
    'Ce paiement n’est plus disponible. Retournez dans PhotoKeeper pour le démarrer ou le reprendre en toute sécurité.',
  pricingUnavailable:
    'Les prix sont temporairement indisponibles. Retournez dans PhotoKeeper et réessayez dans quelques instants.',
  offers: {
    personal: {
      name: 'PhotoKeeper Personal à vie',
      description:
        'Une licence PhotoKeeper à vie avec paiement unique. Elle associe définitivement un compte iCloud.',
      features: [
        'Sauvegarde des contenus de votre compte Photos iCloud authentifié',
        'Stockage local sur votre ordinateur',
        'Un compte iCloud associé définitivement',
        'Paiement unique',
      ],
      cta: 'Acheter Personal à vie',
    },
    family: {
      name: 'PhotoKeeper Family à vie',
      description:
        'Une licence PhotoKeeper à vie avec paiement unique. Elle associe définitivement jusqu’à trois comptes iCloud.',
      features: [
        'Sauvegarde des contenus de comptes Photos iCloud authentifiés',
        'Stockage local sur votre ordinateur',
        'Jusqu’à trois comptes iCloud associés définitivement',
        'Paiement unique',
      ],
      cta: 'Acheter Family à vie',
    },
    upgrade: {
      name: 'Mise à niveau PhotoKeeper Personal vers Family',
      description:
        'Convertit une licence Personal à vie active en Family et augmente la limite permanente d’un à trois comptes. Ce produit n’est pas une licence autonome.',
      features: [
        'Nécessite une licence Personal à vie active',
        'Conserve la licence et l’association permanente existantes',
        'Porte la limite d’un à trois comptes',
        'Paiement unique',
      ],
      cta: 'Passer à Family',
    },
  },
};

const deDE: CheckoutCopy = {
  metaTitle: 'PhotoKeeper-Lebenszeitlizenzen',
  metaDescription: 'PhotoKeeper-Lebenszeitlizenzen und sichere Zahlung mit Paddle.',
  languageLabel: 'Sprache',
  heroEyebrow: 'PhotoKeeper für Windows',
  heroTitle: 'Ihre Fotos immer griffbereit. Einmal bezahlen.',
  heroDescription:
    'Sichern Sie Medien aus Ihrem eigenen authentifizierten iCloud-Fotos-Account in einem von Ihnen kontrollierten Speicher.',
  sandboxBadge: 'Sandbox-Testzahlung · Keine echte Abbuchung',
  offersLabel: 'PhotoKeeper-Lebenszeitlizenzen',
  loadingPrices: 'Lokale Preise für Ihr Land werden geladen…',
  localizedPriceReady:
    'Der angezeigte Gesamtbetrag wird von Paddle direkt für Ihren Standort bereitgestellt.',
  chooseInPhotoKeeper:
    'Wählen Sie in PhotoKeeper eine Lizenz aus, um einen dauerhaft gebundenen Checkout zu erstellen.',
  priceUnavailable: 'Preis nicht verfügbar',
  selectedBadge: 'In PhotoKeeper ausgewählt',
  oneTimeLicense: 'Lebenszeitlizenz',
  licenseUpgrade: 'Lizenz-Upgrade',
  oneTimeQuantity: 'Einmalzahlung · Menge 1',
  signInToUpgrade: 'Zum Upgrade anmelden',
  upgradeEyebrow: 'Sie haben bereits Personal?',
  upgradeTitle: 'Vorhandene Lizenz upgraden',
  upgradeDescription:
    'Der PhotoKeeper-Lizenzdienst prüft Ihre Upgrade-Berechtigung, bevor eine Transaktion erstellt wird.',
  trustTitle: 'Lokal zuerst konzipiert',
  trustDescription:
    'PhotoKeeper speichert Sicherungen auf dem von Ihnen gewählten lokalen Speicher. Paddle verarbeitet die Zahlung, erhält jedoch niemals Ihre iCloud-Zugangsdaten oder Authentifizierungstoken.',
  disclaimer:
    'PhotoKeeper wird unabhängig entwickelt und ist weder mit Apple Inc. verbunden noch von Apple gesponsert oder empfohlen.',
  paymentCompleted: 'Zahlung abgeschlossen. PhotoKeeper überprüft Ihre Lizenz sicher.',
  checkoutError:
    'Die Zahlung konnte nicht abgeschlossen werden. Prüfen Sie die Zahlungsdaten und versuchen Sie es erneut.',
  priceLoadError:
    'Paddle konnte die lokalen Preise nicht laden. Der Checkout ist deaktiviert; kehren Sie zu PhotoKeeper zurück und versuchen Sie es erneut.',
  upgradeRequiresSignIn:
    'Melden Sie sich in PhotoKeeper mit dem iCloud-Account an, der mit Ihrer aktiven Personal-Lizenz verbunden ist, um das Upgrade durchzuführen.',
  purchaseRequiresSignIn:
    'Starten Sie den Kauf nach der Authentifizierung Ihres iCloud-Accounts in PhotoKeeper, damit der Account dauerhaft gebunden wird.',
  openingCheckout: 'Sicherer Paddle-Checkout wird geöffnet…',
  loadingTitle: 'PhotoKeeper-Preise werden vorbereitet',
  loadingDescription: 'Sichere Sandbox-Konfiguration und lokale Gesamtbeträge werden geladen…',
  welcomeEyebrow: 'Zahlung erhalten',
  welcomeTitle: 'Zu PhotoKeeper zurückkehren',
  welcomeDescription:
    'PhotoKeeper wird erst freigeschaltet oder aktualisiert, nachdem der Lizenzdienst den signierten transaction.completed-Webhook geprüft hat. Dies dauert normalerweise nur wenige Sekunden.',
  welcomeClose: 'Sie können diesen Browser-Tab jetzt sicher schließen.',
  fatalTitle: 'Checkout nicht verfügbar',
  invalidLink:
    'Dieser Checkout-Link ist ungültig. Kehren Sie zu PhotoKeeper zurück und versuchen Sie es erneut.',
  checkoutGone:
    'Dieser Checkout ist nicht mehr verfügbar. Kehren Sie zu PhotoKeeper zurück, um ihn sicher neu zu starten oder fortzusetzen.',
  pricingUnavailable:
    'Die Preise sind vorübergehend nicht verfügbar. Kehren Sie in Kürze zu PhotoKeeper zurück und versuchen Sie es erneut.',
  offers: {
    personal: {
      name: 'PhotoKeeper Personal Lebenszeit',
      description:
        'Eine PhotoKeeper-Lebenszeitlizenz mit Einmalzahlung. Sie wird dauerhaft mit einem iCloud-Account verbunden.',
      features: [
        'Medien aus Ihrem authentifizierten iCloud-Fotos-Account sichern',
        'Lokaler Speicher auf Ihrem Computer',
        'Ein dauerhaft verbundener iCloud-Account',
        'Einmalzahlung',
      ],
      cta: 'Personal Lebenszeit kaufen',
    },
    family: {
      name: 'PhotoKeeper Family Lebenszeit',
      description:
        'Eine PhotoKeeper-Lebenszeitlizenz mit Einmalzahlung. Sie wird dauerhaft mit bis zu drei iCloud-Accounts verbunden.',
      features: [
        'Medien aus authentifizierten iCloud-Fotos-Accounts sichern',
        'Lokaler Speicher auf Ihrem Computer',
        'Bis zu drei dauerhaft verbundene iCloud-Accounts',
        'Einmalzahlung',
      ],
      cta: 'Family Lebenszeit kaufen',
    },
    upgrade: {
      name: 'PhotoKeeper-Upgrade von Personal auf Family',
      description:
        'Wandelt eine aktive Personal-Lebenszeitlizenz in Family um und erhöht das dauerhafte Account-Limit von eins auf drei. Dies ist keine eigenständige Lizenz.',
      features: [
        'Aktive Personal-Lebenszeitlizenz erforderlich',
        'Bestehende Lizenz und dauerhafte Bindung bleiben erhalten',
        'Erhöht das Account-Limit von eins auf drei',
        'Einmalzahlung',
      ],
      cta: 'Auf Family upgraden',
    },
  },
};

const itIT: CheckoutCopy = {
  metaTitle: 'Licenze a vita PhotoKeeper',
  metaDescription: 'Licenze a vita PhotoKeeper e pagamento sicuro con Paddle.',
  languageLabel: 'Lingua',
  heroEyebrow: 'PhotoKeeper per Windows',
  heroTitle: 'Le tue foto sempre con te. Paghi una volta sola.',
  heroDescription:
    'Esegui il backup dei contenuti dal tuo account Foto di iCloud autenticato a uno spazio di archiviazione sotto il tuo controllo.',
  sandboxBadge: 'Pagamento di prova Sandbox · Nessun addebito reale',
  offersLabel: 'Licenze a vita PhotoKeeper',
  loadingPrices: 'Caricamento dei prezzi locali per il tuo Paese…',
  localizedPriceReady:
    'Il totale mostrato proviene direttamente da Paddle in base alla tua posizione.',
  chooseInPhotoKeeper:
    'Scegli una licenza in PhotoKeeper per creare un pagamento con associazione permanente.',
  priceUnavailable: 'Prezzo non disponibile',
  selectedBadge: 'Selezionato in PhotoKeeper',
  oneTimeLicense: 'Licenza a vita',
  licenseUpgrade: 'Aggiornamento licenza',
  oneTimeQuantity: 'Pagamento una tantum · Quantità 1',
  signInToUpgrade: 'Accedi per aggiornare',
  upgradeEyebrow: 'Hai già Personal?',
  upgradeTitle: 'Aggiorna la licenza che possiedi',
  upgradeDescription:
    'Il servizio licenze PhotoKeeper verifica l’idoneità all’aggiornamento prima di creare una transazione.',
  trustTitle: 'Progettato per l’archiviazione locale',
  trustDescription:
    'PhotoKeeper salva i backup nello spazio di archiviazione locale scelto. Paddle gestisce il pagamento, ma non riceve mai le credenziali o i token di autenticazione iCloud.',
  disclaimer:
    'PhotoKeeper è sviluppato in modo indipendente e non è affiliato, sponsorizzato o approvato da Apple Inc.',
  paymentCompleted: 'Pagamento completato. PhotoKeeper sta verificando la licenza in modo sicuro.',
  checkoutError: 'Impossibile completare il pagamento. Controlla i dati e riprova.',
  priceLoadError:
    'Paddle non ha potuto caricare i prezzi locali. Il pagamento è disattivato; torna a PhotoKeeper e riprova.',
  upgradeRequiresSignIn:
    'Accedi a PhotoKeeper con l’account iCloud associato alla licenza Personal attiva per eseguire l’aggiornamento.',
  purchaseRequiresSignIn:
    'Per associare l’account in modo permanente, avvia l’acquisto in PhotoKeeper dopo aver autenticato il tuo account iCloud.',
  openingCheckout: 'Apertura del pagamento sicuro Paddle…',
  loadingTitle: 'Preparazione dei prezzi PhotoKeeper',
  loadingDescription: 'Caricamento della configurazione Sandbox sicura e dei totali locali…',
  welcomeEyebrow: 'Pagamento ricevuto',
  welcomeTitle: 'Torna a PhotoKeeper',
  welcomeDescription:
    'PhotoKeeper verrà sbloccato o aggiornato solo dopo che il servizio licenze avrà verificato il webhook transaction.completed firmato. Di solito bastano pochi secondi.',
  welcomeClose: 'Ora puoi chiudere questa scheda del browser in sicurezza.',
  fatalTitle: 'Pagamento non disponibile',
  invalidLink: 'Questo link di pagamento non è valido. Torna a PhotoKeeper e riprova.',
  checkoutGone:
    'Questo pagamento non è più disponibile. Torna a PhotoKeeper per avviarlo o riprenderlo in sicurezza.',
  pricingUnavailable:
    'I prezzi sono temporaneamente non disponibili. Torna a PhotoKeeper e riprova tra poco.',
  offers: {
    personal: {
      name: 'PhotoKeeper Personal a vita',
      description:
        'Una licenza a vita per PhotoKeeper con pagamento una tantum. Associa permanentemente un account iCloud.',
      features: [
        'Backup dei contenuti dal tuo account Foto di iCloud autenticato',
        'Archiviazione locale sul tuo computer',
        'Un account iCloud associato in modo permanente',
        'Pagamento una tantum',
      ],
      cta: 'Acquista Personal a vita',
    },
    family: {
      name: 'PhotoKeeper Family a vita',
      description:
        'Una licenza a vita per PhotoKeeper con pagamento una tantum. Associa permanentemente fino a tre account iCloud.',
      features: [
        'Backup dei contenuti da account Foto di iCloud autenticati',
        'Archiviazione locale sul tuo computer',
        'Fino a tre account iCloud associati in modo permanente',
        'Pagamento una tantum',
      ],
      cta: 'Acquista Family a vita',
    },
    upgrade: {
      name: 'Aggiornamento PhotoKeeper da Personal a Family',
      description:
        'Converte una licenza Personal a vita attiva in Family e aumenta il limite permanente da uno a tre account. Non è una licenza autonoma.',
      features: [
        'Richiede una licenza Personal a vita attiva',
        'Mantiene la licenza e l’associazione permanente esistenti',
        'Aumenta il limite da uno a tre account',
        'Pagamento una tantum',
      ],
      cta: 'Aggiorna a Family',
    },
  },
};

const nlNL: CheckoutCopy = {
  metaTitle: 'Levenslange PhotoKeeper-licenties',
  metaDescription: 'Levenslange PhotoKeeper-licenties en veilig afrekenen met Paddle.',
  languageLabel: 'Taal',
  heroEyebrow: 'PhotoKeeper voor Windows',
  heroTitle: 'Houd je foto’s dichtbij. Betaal één keer.',
  heroDescription:
    'Maak een back-up van media uit je eigen geverifieerde iCloud-foto’s-account naar opslag die je zelf beheert.',
  sandboxBadge: 'Sandbox-testbetaling · Geen echte betaling',
  offersLabel: 'Levenslange PhotoKeeper-licenties',
  loadingPrices: 'Lokale prijzen voor jouw land laden…',
  localizedPriceReady:
    'Het weergegeven totaal komt rechtstreeks van Paddle op basis van je locatie.',
  chooseInPhotoKeeper:
    'Kies een licentie in PhotoKeeper om een permanent gekoppelde betaling te maken.',
  priceUnavailable: 'Prijs niet beschikbaar',
  selectedBadge: 'Geselecteerd in PhotoKeeper',
  oneTimeLicense: 'Levenslange licentie',
  licenseUpgrade: 'Licentie-upgrade',
  oneTimeQuantity: 'Eenmalige betaling · Aantal 1',
  signInToUpgrade: 'Log in om te upgraden',
  upgradeEyebrow: 'Heb je Personal al?',
  upgradeTitle: 'Upgrade je bestaande licentie',
  upgradeDescription:
    'De PhotoKeeper-licentieservice controleert of je in aanmerking komt voordat een transactie wordt gemaakt.',
  trustTitle: 'Ontworpen voor lokale opslag',
  trustDescription:
    'PhotoKeeper bewaart back-ups op de lokale opslag die je kiest. Paddle verwerkt de betaling, maar ontvangt nooit je iCloud-inloggegevens of authenticatietokens.',
  disclaimer:
    'PhotoKeeper is onafhankelijk ontwikkeld en is niet gelieerd aan, gesponsord door of goedgekeurd door Apple Inc.',
  paymentCompleted: 'Betaling voltooid. PhotoKeeper controleert je licentie veilig.',
  checkoutError:
    'Afrekenen is niet gelukt. Controleer de betalingsgegevens en probeer het opnieuw.',
  priceLoadError:
    'Paddle kon de lokale prijzen niet laden. Afrekenen is uitgeschakeld; ga terug naar PhotoKeeper en probeer het opnieuw.',
  upgradeRequiresSignIn:
    'Log in bij PhotoKeeper met het iCloud-account dat aan je actieve Personal-licentie is gekoppeld om te upgraden.',
  purchaseRequiresSignIn:
    'Start de aankoop in PhotoKeeper nadat je je iCloud-account hebt geverifieerd, zodat het account permanent wordt gekoppeld.',
  openingCheckout: 'Veilige Paddle-betaalpagina openen…',
  loadingTitle: 'PhotoKeeper-prijzen voorbereiden',
  loadingDescription: 'Veilige Sandbox-configuratie en lokale totalen laden…',
  welcomeEyebrow: 'Betaling ontvangen',
  welcomeTitle: 'Ga terug naar PhotoKeeper',
  welcomeDescription:
    'PhotoKeeper wordt pas ontgrendeld of geüpgraded nadat de licentieservice de ondertekende transaction.completed-webhook heeft gecontroleerd. Dit duurt meestal enkele seconden.',
  welcomeClose: 'Je kunt dit browsertabblad nu veilig sluiten.',
  fatalTitle: 'Afrekenen niet beschikbaar',
  invalidLink: 'Deze betaallink is ongeldig. Ga terug naar PhotoKeeper en probeer het opnieuw.',
  checkoutGone:
    'Deze betaling is niet meer beschikbaar. Ga terug naar PhotoKeeper om veilig opnieuw te starten of door te gaan.',
  pricingUnavailable:
    'Prijzen zijn tijdelijk niet beschikbaar. Ga binnenkort terug naar PhotoKeeper en probeer het opnieuw.',
  offers: {
    personal: {
      name: 'PhotoKeeper Personal levenslang',
      description:
        'Een levenslange PhotoKeeper-licentie met eenmalige betaling. Koppelt permanent één iCloud-account.',
      features: [
        'Media back-uppen uit je geverifieerde iCloud-foto’s-account',
        'Lokale opslag op je Windows-pc',
        'Eén permanent gekoppeld iCloud-account',
        'Eenmalige betaling',
      ],
      cta: 'Personal levenslang kopen',
    },
    family: {
      name: 'PhotoKeeper Family levenslang',
      description:
        'Een levenslange PhotoKeeper-licentie met eenmalige betaling. Koppelt permanent maximaal drie iCloud-accounts.',
      features: [
        'Media back-uppen uit geverifieerde iCloud-foto’s-accounts',
        'Lokale opslag op je Windows-pc',
        'Maximaal drie permanent gekoppelde iCloud-accounts',
        'Eenmalige betaling',
      ],
      cta: 'Family levenslang kopen',
    },
    upgrade: {
      name: 'PhotoKeeper-upgrade van Personal naar Family',
      description:
        'Zet een actieve levenslange Personal-licentie om naar Family en verhoogt de permanente accountlimiet van één naar drie. Dit is geen zelfstandige licentie.',
      features: [
        'Vereist een actieve levenslange Personal-licentie',
        'Behoudt de bestaande licentie en permanente koppeling',
        'Verhoogt de accountlimiet van één naar drie',
        'Eenmalige betaling',
      ],
      cta: 'Upgraden naar Family',
    },
  },
};

const plPL: CheckoutCopy = {
  metaTitle: 'Dożywotnie licencje PhotoKeeper',
  metaDescription: 'Dożywotnie licencje PhotoKeeper i bezpieczna płatność Paddle.',
  languageLabel: 'Język',
  heroEyebrow: 'PhotoKeeper dla Windows',
  heroTitle: 'Miej zdjęcia zawsze pod ręką. Zapłać tylko raz.',
  heroDescription:
    'Twórz kopie multimediów z własnego uwierzytelnionego konta Zdjęcia iCloud w pamięci, nad którą masz kontrolę.',
  sandboxBadge: 'Płatność testowa Sandbox · Bez prawdziwej opłaty',
  offersLabel: 'Dożywotnie licencje PhotoKeeper',
  loadingPrices: 'Wczytywanie cen lokalnych dla Twojego kraju…',
  localizedPriceReady:
    'Wyświetlana suma pochodzi bezpośrednio od Paddle i zależy od Twojej lokalizacji.',
  chooseInPhotoKeeper:
    'Wybierz licencję w PhotoKeeper, aby utworzyć płatność z trwałym powiązaniem.',
  priceUnavailable: 'Cena niedostępna',
  selectedBadge: 'Wybrano w PhotoKeeper',
  oneTimeLicense: 'Licencja dożywotnia',
  licenseUpgrade: 'Uaktualnienie licencji',
  oneTimeQuantity: 'Płatność jednorazowa · Ilość 1',
  signInToUpgrade: 'Zaloguj się, aby uaktualnić',
  upgradeEyebrow: 'Masz już wersję Personal?',
  upgradeTitle: 'Uaktualnij posiadaną licencję',
  upgradeDescription:
    'Usługa licencyjna PhotoKeeper sprawdza uprawnienie do uaktualnienia przed utworzeniem transakcji.',
  trustTitle: 'Zaprojektowano z myślą o lokalnym przechowywaniu',
  trustDescription:
    'PhotoKeeper zapisuje kopie w wybranej pamięci lokalnej. Paddle obsługuje płatność, ale nigdy nie otrzymuje danych logowania ani tokenów uwierzytelniania iCloud.',
  disclaimer:
    'PhotoKeeper jest rozwijany niezależnie i nie jest powiązany, sponsorowany ani wspierany przez Apple Inc.',
  paymentCompleted: 'Płatność zakończona. PhotoKeeper bezpiecznie weryfikuje licencję.',
  checkoutError: 'Nie udało się zakończyć płatności. Sprawdź dane płatności i spróbuj ponownie.',
  priceLoadError:
    'Paddle nie mogło wczytać cen lokalnych. Płatność jest wyłączona; wróć do PhotoKeeper i spróbuj ponownie.',
  upgradeRequiresSignIn:
    'Zaloguj się w PhotoKeeper przy użyciu konta iCloud powiązanego z aktywną licencją Personal, aby ją uaktualnić.',
  purchaseRequiresSignIn:
    'Aby trwale powiązać konto, rozpocznij zakup w PhotoKeeper po uwierzytelnieniu konta iCloud.',
  openingCheckout: 'Otwieranie bezpiecznej płatności Paddle…',
  loadingTitle: 'Przygotowywanie cen PhotoKeeper',
  loadingDescription: 'Wczytywanie bezpiecznej konfiguracji Sandbox i lokalnych sum…',
  welcomeEyebrow: 'Płatność otrzymana',
  welcomeTitle: 'Wróć do PhotoKeeper',
  welcomeDescription:
    'PhotoKeeper zostanie odblokowany lub uaktualniony dopiero po zweryfikowaniu przez usługę licencyjną podpisanego webhooka transaction.completed. Zwykle trwa to kilka sekund.',
  welcomeClose: 'Możesz bezpiecznie zamknąć tę kartę przeglądarki.',
  fatalTitle: 'Płatność niedostępna',
  invalidLink: 'Ten link płatności jest nieprawidłowy. Wróć do PhotoKeeper i spróbuj ponownie.',
  checkoutGone:
    'Ta płatność nie jest już dostępna. Wróć do PhotoKeeper, aby bezpiecznie rozpocząć lub wznowić operację.',
  pricingUnavailable:
    'Ceny są chwilowo niedostępne. Wróć za moment do PhotoKeeper i spróbuj ponownie.',
  offers: {
    personal: {
      name: 'PhotoKeeper Personal na zawsze',
      description:
        'Dożywotnia licencja PhotoKeeper z jednorazową płatnością. Trwale wiąże jedno konto iCloud.',
      features: [
        'Kopia multimediów z uwierzytelnionego konta Zdjęcia iCloud',
        'Pamięć lokalna na komputerze z Windows',
        'Jedno trwale powiązane konto iCloud',
        'Płatność jednorazowa',
      ],
      cta: 'Kup Personal na zawsze',
    },
    family: {
      name: 'PhotoKeeper Family na zawsze',
      description:
        'Dożywotnia licencja PhotoKeeper z jednorazową płatnością. Trwale wiąże do trzech kont iCloud.',
      features: [
        'Kopia multimediów z uwierzytelnionych kont Zdjęcia iCloud',
        'Pamięć lokalna na komputerze z Windows',
        'Do trzech trwale powiązanych kont iCloud',
        'Płatność jednorazowa',
      ],
      cta: 'Kup Family na zawsze',
    },
    upgrade: {
      name: 'Uaktualnienie PhotoKeeper Personal do Family',
      description:
        'Zmienia aktywną dożywotnią licencję Personal na Family i zwiększa stały limit kont z jednego do trzech. Nie jest to samodzielna licencja.',
      features: [
        'Wymaga aktywnej dożywotniej licencji Personal',
        'Zachowuje istniejącą licencję i trwałe powiązanie',
        'Zwiększa limit kont z jednego do trzech',
        'Płatność jednorazowa',
      ],
      cta: 'Uaktualnij do Family',
    },
  },
};

const daDK: CheckoutCopy = {
  metaTitle: 'PhotoKeeper-livstidslicenser',
  metaDescription: 'PhotoKeeper-livstidslicenser og sikker betaling med Paddle.',
  languageLabel: 'Sprog',
  heroEyebrow: 'PhotoKeeper til Windows',
  heroTitle: 'Hold dine billeder tæt på. Betal én gang.',
  heroDescription:
    'Sikkerhedskopiér medier fra din egen godkendte iCloud-fotos-konto til lagerplads, du selv styrer.',
  sandboxBadge: 'Sandbox-testbetaling · Ingen rigtig betaling',
  offersLabel: 'PhotoKeeper-livstidslicenser',
  loadingPrices: 'Indlæser lokale priser for dit land…',
  localizedPriceReady: 'Det viste totalbeløb kommer direkte fra Paddle ud fra din placering.',
  chooseInPhotoKeeper:
    'Vælg en licens i PhotoKeeper for at oprette en betaling med permanent tilknytning.',
  priceUnavailable: 'Pris ikke tilgængelig',
  selectedBadge: 'Valgt i PhotoKeeper',
  oneTimeLicense: 'Livstidslicens',
  licenseUpgrade: 'Licensopgradering',
  oneTimeQuantity: 'Engangsbetaling · Antal 1',
  signInToUpgrade: 'Log ind for at opgradere',
  upgradeEyebrow: 'Har du allerede Personal?',
  upgradeTitle: 'Opgrader din nuværende licens',
  upgradeDescription:
    'PhotoKeepers licenstjeneste kontrollerer din ret til opgradering, før der oprettes en transaktion.',
  trustTitle: 'Designet til lokal lagring',
  trustDescription:
    'PhotoKeeper gemmer sikkerhedskopier på den lokale lagerplads, du vælger. Paddle håndterer betalingen, men modtager aldrig dine iCloud-loginoplysninger eller godkendelsestokens.',
  disclaimer:
    'PhotoKeeper er udviklet uafhængigt og er ikke tilknyttet, sponsoreret eller godkendt af Apple Inc.',
  paymentCompleted: 'Betalingen er gennemført. PhotoKeeper kontrollerer din licens sikkert.',
  checkoutError:
    'Betalingen kunne ikke gennemføres. Kontrollér betalingsoplysningerne, og prøv igen.',
  priceLoadError:
    'Paddle kunne ikke indlæse de lokale priser. Betaling er deaktiveret; gå tilbage til PhotoKeeper, og prøv igen.',
  upgradeRequiresSignIn:
    'Log ind i PhotoKeeper med den iCloud-konto, der er knyttet til din aktive Personal-licens, for at opgradere.',
  purchaseRequiresSignIn:
    'Start købet i PhotoKeeper efter godkendelse af din iCloud-konto for at knytte kontoen permanent.',
  openingCheckout: 'Åbner sikker Paddle-betaling…',
  loadingTitle: 'Forbereder PhotoKeeper-priser',
  loadingDescription: 'Indlæser sikker Sandbox-konfiguration og lokale totalbeløb…',
  welcomeEyebrow: 'Betaling modtaget',
  welcomeTitle: 'Gå tilbage til PhotoKeeper',
  welcomeDescription:
    'PhotoKeeper låses først op eller opgraderes, når licenstjenesten har bekræftet den signerede transaction.completed-webhook. Det tager normalt få sekunder.',
  welcomeClose: 'Du kan nu lukke denne browserfane sikkert.',
  fatalTitle: 'Betaling ikke tilgængelig',
  invalidLink: 'Dette betalingslink er ugyldigt. Gå tilbage til PhotoKeeper, og prøv igen.',
  checkoutGone:
    'Denne betaling er ikke længere tilgængelig. Gå tilbage til PhotoKeeper for sikkert at starte eller fortsætte.',
  pricingUnavailable:
    'Priserne er midlertidigt utilgængelige. Gå snart tilbage til PhotoKeeper, og prøv igen.',
  offers: {
    personal: {
      name: 'PhotoKeeper Personal Livstid',
      description:
        'En PhotoKeeper-livstidslicens med engangsbetaling. Knytter én iCloud-konto permanent.',
      features: [
        'Sikkerhedskopi af medier fra din godkendte iCloud-fotos-konto',
        'Lokal lagring på din Windows-pc',
        'Én permanent tilknyttet iCloud-konto',
        'Engangsbetaling',
      ],
      cta: 'Køb Personal Livstid',
    },
    family: {
      name: 'PhotoKeeper Family Livstid',
      description:
        'En PhotoKeeper-livstidslicens med engangsbetaling. Knytter op til tre iCloud-konti permanent.',
      features: [
        'Sikkerhedskopi af medier fra godkendte iCloud-fotos-konti',
        'Lokal lagring på din Windows-pc',
        'Op til tre permanent tilknyttede iCloud-konti',
        'Engangsbetaling',
      ],
      cta: 'Køb Family Livstid',
    },
    upgrade: {
      name: 'PhotoKeeper-opgradering fra Personal til Family',
      description:
        'Konverterer en aktiv Personal-livstidslicens til Family og hæver den permanente kontogrænse fra én til tre. Dette er ikke en selvstændig licens.',
      features: [
        'Kræver en aktiv Personal-livstidslicens',
        'Bevarer eksisterende licens og permanent tilknytning',
        'Hæver kontogrænsen fra én til tre',
        'Engangsbetaling',
      ],
      cta: 'Opgrader til Family',
    },
  },
};

const svSE: CheckoutCopy = {
  metaTitle: 'PhotoKeeper-livstidslicenser',
  metaDescription: 'PhotoKeeper-livstidslicenser och säker betalning med Paddle.',
  languageLabel: 'Språk',
  heroEyebrow: 'PhotoKeeper för Windows',
  heroTitle: 'Ha dina bilder nära. Betala en gång.',
  heroDescription:
    'Säkerhetskopiera media från ditt eget autentiserade iCloud-bilder-konto till lagring som du själv styr över.',
  sandboxBadge: 'Sandbox-testbetalning · Ingen riktig debitering',
  offersLabel: 'PhotoKeeper-livstidslicenser',
  loadingPrices: 'Läser in lokala priser för ditt land…',
  localizedPriceReady: 'Totalsumman som visas kommer direkt från Paddle utifrån din plats.',
  chooseInPhotoKeeper:
    'Välj en licens i PhotoKeeper för att skapa en betalning med permanent koppling.',
  priceUnavailable: 'Priset är inte tillgängligt',
  selectedBadge: 'Vald i PhotoKeeper',
  oneTimeLicense: 'Livstidslicens',
  licenseUpgrade: 'Licensuppgradering',
  oneTimeQuantity: 'Engångsbetalning · Antal 1',
  signInToUpgrade: 'Logga in för att uppgradera',
  upgradeEyebrow: 'Har du redan Personal?',
  upgradeTitle: 'Uppgradera licensen du redan har',
  upgradeDescription:
    'PhotoKeepers licenstjänst kontrollerar att du kan uppgradera innan en transaktion skapas.',
  trustTitle: 'Utformad för lokal lagring',
  trustDescription:
    'PhotoKeeper sparar säkerhetskopior på den lokala lagring du väljer. Paddle hanterar betalningen men tar aldrig emot dina iCloud-inloggningsuppgifter eller autentiseringstoken.',
  disclaimer:
    'PhotoKeeper utvecklas oberoende och är inte anslutet till, sponsrat eller godkänt av Apple Inc.',
  paymentCompleted: 'Betalningen är klar. PhotoKeeper verifierar din licens säkert.',
  checkoutError:
    'Betalningen kunde inte slutföras. Kontrollera betalningsuppgifterna och försök igen.',
  priceLoadError:
    'Paddle kunde inte läsa in lokala priser. Betalningen är inaktiverad; gå tillbaka till PhotoKeeper och försök igen.',
  upgradeRequiresSignIn:
    'Logga in i PhotoKeeper med iCloud-kontot som är kopplat till din aktiva Personal-licens för att uppgradera.',
  purchaseRequiresSignIn:
    'Starta köpet i PhotoKeeper efter att du autentiserat ditt iCloud-konto för att koppla kontot permanent.',
  openingCheckout: 'Öppnar säker Paddle-betalning…',
  loadingTitle: 'Förbereder PhotoKeeper-priser',
  loadingDescription: 'Läser in säker Sandbox-konfiguration och lokala totalsummor…',
  welcomeEyebrow: 'Betalning mottagen',
  welcomeTitle: 'Gå tillbaka till PhotoKeeper',
  welcomeDescription:
    'PhotoKeeper låses upp eller uppgraderas först efter att licenstjänsten har verifierat den signerade transaction.completed-webhooken. Det tar vanligtvis några sekunder.',
  welcomeClose: 'Du kan nu stänga den här webbläsarfliken säkert.',
  fatalTitle: 'Betalning inte tillgänglig',
  invalidLink: 'Den här betalningslänken är ogiltig. Gå tillbaka till PhotoKeeper och försök igen.',
  checkoutGone:
    'Den här betalningen är inte längre tillgänglig. Gå tillbaka till PhotoKeeper för att säkert starta eller fortsätta.',
  pricingUnavailable:
    'Priserna är tillfälligt otillgängliga. Gå snart tillbaka till PhotoKeeper och försök igen.',
  offers: {
    personal: {
      name: 'PhotoKeeper Personal Livstid',
      description:
        'En PhotoKeeper-livstidslicens med engångsbetalning. Kopplar permanent ett iCloud-konto.',
      features: [
        'Säkerhetskopiera media från ditt autentiserade iCloud-bilder-konto',
        'Lokal lagring på din Windows-dator',
        'Ett permanent kopplat iCloud-konto',
        'Engångsbetalning',
      ],
      cta: 'Köp Personal Livstid',
    },
    family: {
      name: 'PhotoKeeper Family Livstid',
      description:
        'En PhotoKeeper-livstidslicens med engångsbetalning. Kopplar permanent upp till tre iCloud-konton.',
      features: [
        'Säkerhetskopiera media från autentiserade iCloud-bilder-konton',
        'Lokal lagring på din Windows-dator',
        'Upp till tre permanent kopplade iCloud-konton',
        'Engångsbetalning',
      ],
      cta: 'Köp Family Livstid',
    },
    upgrade: {
      name: 'PhotoKeeper-uppgradering från Personal till Family',
      description:
        'Konverterar en aktiv Personal-livstidslicens till Family och ökar den permanenta kontogränsen från ett till tre. Detta är inte en fristående licens.',
      features: [
        'Kräver en aktiv Personal-livstidslicens',
        'Behåller befintlig licens och permanent koppling',
        'Ökar kontogränsen från ett till tre',
        'Engångsbetalning',
      ],
      cta: 'Uppgradera till Family',
    },
  },
};

const nbNO: CheckoutCopy = {
  metaTitle: 'PhotoKeeper-livstidslisenser',
  metaDescription: 'PhotoKeeper-livstidslisenser og sikker betaling med Paddle.',
  languageLabel: 'Språk',
  heroEyebrow: 'PhotoKeeper for Windows',
  heroTitle: 'Ha bildene dine i nærheten. Betal én gang.',
  heroDescription:
    'Sikkerhetskopier medier fra din egen autentiserte iCloud-bilder-konto til lagring du selv kontrollerer.',
  sandboxBadge: 'Sandbox-testbetaling · Ingen ekte belastning',
  offersLabel: 'PhotoKeeper-livstidslisenser',
  loadingPrices: 'Laster inn lokale priser for landet ditt…',
  localizedPriceReady:
    'Totalsummen som vises, kommer direkte fra Paddle basert på plasseringen din.',
  chooseInPhotoKeeper:
    'Velg en lisens i PhotoKeeper for å opprette en betaling med permanent tilknytning.',
  priceUnavailable: 'Pris ikke tilgjengelig',
  selectedBadge: 'Valgt i PhotoKeeper',
  oneTimeLicense: 'Livstidslisens',
  licenseUpgrade: 'Lisensoppgradering',
  oneTimeQuantity: 'Engangsbetaling · Antall 1',
  signInToUpgrade: 'Logg inn for å oppgradere',
  upgradeEyebrow: 'Har du allerede Personal?',
  upgradeTitle: 'Oppgrader lisensen du allerede har',
  upgradeDescription:
    'PhotoKeepers lisenstjeneste kontrollerer om du kan oppgradere før en transaksjon opprettes.',
  trustTitle: 'Utformet for lokal lagring',
  trustDescription:
    'PhotoKeeper lagrer sikkerhetskopier på den lokale lagringen du velger. Paddle håndterer betalingen, men mottar aldri iCloud-påloggingsinformasjon eller autentiseringstokener.',
  disclaimer:
    'PhotoKeeper er utviklet uavhengig og er ikke tilknyttet, sponset eller godkjent av Apple Inc.',
  paymentCompleted:
    'Betalingen er fullført. PhotoKeeper verifiserer lisensen din på en sikker måte.',
  checkoutError:
    'Betalingen kunne ikke fullføres. Kontroller betalingsinformasjonen og prøv igjen.',
  priceLoadError:
    'Paddle kunne ikke laste inn lokale priser. Betalingen er deaktivert; gå tilbake til PhotoKeeper og prøv igjen.',
  upgradeRequiresSignIn:
    'Logg inn i PhotoKeeper med iCloud-kontoen som er knyttet til den aktive Personal-lisensen din for å oppgradere.',
  purchaseRequiresSignIn:
    'Start kjøpet i PhotoKeeper etter at du har autentisert iCloud-kontoen for å knytte kontoen permanent.',
  openingCheckout: 'Åpner sikker Paddle-betaling…',
  loadingTitle: 'Forbereder PhotoKeeper-priser',
  loadingDescription: 'Laster inn sikker Sandbox-konfigurasjon og lokale totalsummer…',
  welcomeEyebrow: 'Betaling mottatt',
  welcomeTitle: 'Gå tilbake til PhotoKeeper',
  welcomeDescription:
    'PhotoKeeper låses opp eller oppgraderes først etter at lisenstjenesten har verifisert den signerte transaction.completed-webhooken. Dette tar vanligvis noen sekunder.',
  welcomeClose: 'Du kan nå lukke denne nettleserfanen på en trygg måte.',
  fatalTitle: 'Betaling ikke tilgjengelig',
  invalidLink: 'Denne betalingslenken er ugyldig. Gå tilbake til PhotoKeeper og prøv igjen.',
  checkoutGone:
    'Denne betalingen er ikke lenger tilgjengelig. Gå tilbake til PhotoKeeper for å starte eller fortsette på en trygg måte.',
  pricingUnavailable:
    'Prisene er midlertidig utilgjengelige. Gå snart tilbake til PhotoKeeper og prøv igjen.',
  offers: {
    personal: {
      name: 'PhotoKeeper Personal Livstid',
      description:
        'En PhotoKeeper-livstidslisens med engangsbetaling. Knytter én iCloud-konto permanent.',
      features: [
        'Sikkerhetskopiering av medier fra din autentiserte iCloud-bilder-konto',
        'Lokal lagring på datamaskinen din',
        'Én permanent tilknyttet iCloud-konto',
        'Engangsbetaling',
      ],
      cta: 'Kjøp Personal Livstid',
    },
    family: {
      name: 'PhotoKeeper Family Livstid',
      description:
        'En PhotoKeeper-livstidslisens med engangsbetaling. Knytter opptil tre iCloud-kontoer permanent.',
      features: [
        'Sikkerhetskopiering av medier fra autentiserte iCloud-bilder-kontoer',
        'Lokal lagring på datamaskinen din',
        'Opptil tre permanent tilknyttede iCloud-kontoer',
        'Engangsbetaling',
      ],
      cta: 'Kjøp Family Livstid',
    },
    upgrade: {
      name: 'PhotoKeeper-oppgradering fra Personal til Family',
      description:
        'Konverterer en aktiv Personal-livstidslisens til Family og øker den permanente kontogrensen fra én til tre. Dette er ikke en frittstående lisens.',
      features: [
        'Krever en aktiv Personal-livstidslisens',
        'Beholder eksisterende lisens og permanent tilknytning',
        'Øker kontogrensen fra én til tre',
        'Engangsbetaling',
      ],
      cta: 'Oppgrader til Family',
    },
  },
};

const trTR: CheckoutCopy = {
  metaTitle: 'PhotoKeeper Ömür Boyu Lisansları',
  metaDescription: 'PhotoKeeper ömür boyu lisansları ve güvenli Paddle ödemesi.',
  languageLabel: 'Dil',
  heroEyebrow: 'Windows için PhotoKeeper',
  heroTitle: 'Fotoğraflarınız hep yanınızda. Yalnızca bir kez ödeyin.',
  heroDescription:
    'Kimliği doğrulanmış kendi iCloud Fotoğrafları hesabınızdaki medyayı kontrolünüzdeki depolama alanına yedekleyin.',
  sandboxBadge: 'Sandbox test ödemesi · Gerçek ödeme yok',
  offersLabel: 'PhotoKeeper ömür boyu lisansları',
  loadingPrices: 'Ülkenize özel yerel fiyatlar yükleniyor…',
  localizedPriceReady: 'Gösterilen toplam, konumunuza göre doğrudan Paddle tarafından sağlanır.',
  chooseInPhotoKeeper:
    'Kalıcı olarak bağlanan bir ödeme oluşturmak için PhotoKeeper’da bir lisans seçin.',
  priceUnavailable: 'Fiyat kullanılamıyor',
  selectedBadge: 'PhotoKeeper’da seçildi',
  oneTimeLicense: 'Ömür boyu lisans',
  licenseUpgrade: 'Lisans yükseltmesi',
  oneTimeQuantity: 'Tek seferlik ödeme · Adet 1',
  signInToUpgrade: 'Yükseltmek için oturum açın',
  upgradeEyebrow: 'Zaten Personal kullanıyor musunuz?',
  upgradeTitle: 'Mevcut lisansınızı yükseltin',
  upgradeDescription:
    'PhotoKeeper lisans hizmeti, işlem oluşturmadan önce yükseltme uygunluğunuzu doğrular.',
  trustTitle: 'Yerel depolama öncelikli tasarım',
  trustDescription:
    'PhotoKeeper yedekleri seçtiğiniz yerel depolama alanına kaydeder. Paddle ödemeyi işler ancak iCloud kimlik bilgilerinizi veya doğrulama belirteçlerinizi asla almaz.',
  disclaimer:
    'PhotoKeeper bağımsız olarak geliştirilmiştir; Apple Inc. ile bağlantılı değildir, Apple tarafından desteklenmez veya onaylanmaz.',
  paymentCompleted: 'Ödeme tamamlandı. PhotoKeeper lisansınızı güvenli bir şekilde doğruluyor.',
  checkoutError: 'Ödeme tamamlanamadı. Ödeme bilgilerini kontrol edip yeniden deneyin.',
  priceLoadError:
    'Paddle yerel fiyatları yükleyemedi. Ödeme devre dışı; PhotoKeeper’a dönüp yeniden deneyin.',
  upgradeRequiresSignIn:
    'Yükseltmek için etkin Personal lisansınıza bağlı iCloud hesabıyla PhotoKeeper’da oturum açın.',
  purchaseRequiresSignIn:
    'Hesabı kalıcı olarak bağlamak için iCloud hesabınızı doğruladıktan sonra satın alma işlemini PhotoKeeper içinden başlatın.',
  openingCheckout: 'Güvenli Paddle ödemesi açılıyor…',
  loadingTitle: 'PhotoKeeper fiyatları hazırlanıyor',
  loadingDescription: 'Güvenli Sandbox yapılandırması ve yerel toplamlar yükleniyor…',
  welcomeEyebrow: 'Ödeme alındı',
  welcomeTitle: 'PhotoKeeper’a dönün',
  welcomeDescription:
    'PhotoKeeper yalnızca lisans hizmeti imzalı transaction.completed webhook’unu doğruladıktan sonra açılır veya yükseltilir. Bu işlem genellikle birkaç saniye sürer.',
  welcomeClose: 'Bu tarayıcı sekmesini güvenle kapatabilirsiniz.',
  fatalTitle: 'Ödeme kullanılamıyor',
  invalidLink: 'Bu ödeme bağlantısı geçersiz. PhotoKeeper’a dönüp yeniden deneyin.',
  checkoutGone:
    'Bu ödeme artık kullanılamıyor. Güvenli biçimde başlatmak veya sürdürmek için PhotoKeeper’a dönün.',
  pricingUnavailable:
    'Fiyatlar geçici olarak kullanılamıyor. Kısa süre sonra PhotoKeeper’a dönüp yeniden deneyin.',
  offers: {
    personal: {
      name: 'PhotoKeeper Personal Ömür Boyu',
      description:
        'PhotoKeeper için tek seferlik ödemeli ömür boyu lisans. Bir iCloud hesabına kalıcı olarak bağlanır.',
      features: [
        'Kimliği doğrulanmış iCloud Fotoğrafları hesabınızdaki medyayı yedekleyin',
        'Windows bilgisayarınızda yerel depolama',
        'Kalıcı olarak bağlı bir iCloud hesabı',
        'Tek seferlik ödeme',
      ],
      cta: 'Personal Ömür Boyu satın al',
    },
    family: {
      name: 'PhotoKeeper Family Ömür Boyu',
      description:
        'PhotoKeeper için tek seferlik ödemeli ömür boyu lisans. En fazla üç iCloud hesabına kalıcı olarak bağlanır.',
      features: [
        'Kimliği doğrulanmış iCloud Fotoğrafları hesaplarındaki medyayı yedekleyin',
        'Windows bilgisayarınızda yerel depolama',
        'Kalıcı olarak bağlı en fazla üç iCloud hesabı',
        'Tek seferlik ödeme',
      ],
      cta: 'Family Ömür Boyu satın al',
    },
    upgrade: {
      name: 'PhotoKeeper Personal’dan Family’ye yükseltme',
      description:
        'Etkin bir Personal ömür boyu lisansı Family’ye dönüştürür ve kalıcı hesap sınırını birden üçe çıkarır. Tek başına bir lisans değildir.',
      features: [
        'Etkin bir Personal ömür boyu lisans gerektirir',
        'Mevcut lisansı ve kalıcı bağlantıyı korur',
        'Hesap sınırını birden üçe çıkarır',
        'Tek seferlik ödeme',
      ],
      cta: 'Family’ye yükselt',
    },
  },
};

const ruRU: CheckoutCopy = {
  metaTitle: 'Бессрочные лицензии PhotoKeeper',
  metaDescription: 'Бессрочные лицензии PhotoKeeper и безопасная оплата через Paddle.',
  languageLabel: 'Язык',
  heroEyebrow: 'PhotoKeeper для Windows',
  heroTitle: 'Ваши фотографии всегда рядом. Оплата один раз.',
  heroDescription:
    'Сохраняйте медиафайлы из своей подтверждённой учётной записи «Фото iCloud» в хранилище, которым управляете вы.',
  sandboxBadge: 'Тестовая оплата Sandbox · Без реального списания',
  offersLabel: 'Бессрочные лицензии PhotoKeeper',
  loadingPrices: 'Загружаются локальные цены для вашей страны…',
  localizedPriceReady:
    'Показанная сумма предоставлена Paddle напрямую с учётом вашего местоположения.',
  chooseInPhotoKeeper:
    'Выберите лицензию в PhotoKeeper, чтобы создать оплату с постоянной привязкой.',
  priceUnavailable: 'Цена недоступна',
  selectedBadge: 'Выбрано в PhotoKeeper',
  oneTimeLicense: 'Бессрочная лицензия',
  licenseUpgrade: 'Обновление лицензии',
  oneTimeQuantity: 'Разовый платёж · Количество 1',
  signInToUpgrade: 'Войти для обновления',
  upgradeEyebrow: 'Уже есть Personal?',
  upgradeTitle: 'Обновите имеющуюся лицензию',
  upgradeDescription:
    'Служба лицензирования PhotoKeeper проверяет право на обновление до создания транзакции.',
  trustTitle: 'Локальное хранение прежде всего',
  trustDescription:
    'PhotoKeeper сохраняет резервные копии в выбранном вами локальном хранилище. Paddle обрабатывает оплату, но никогда не получает ваши данные входа или токены аутентификации iCloud.',
  disclaimer:
    'PhotoKeeper разработан независимо и не связан с Apple Inc., не спонсируется и не одобряется ею.',
  paymentCompleted: 'Оплата завершена. PhotoKeeper безопасно проверяет вашу лицензию.',
  checkoutError: 'Не удалось завершить оплату. Проверьте платёжные данные и попробуйте ещё раз.',
  priceLoadError:
    'Paddle не удалось загрузить локальные цены. Оплата отключена; вернитесь в PhotoKeeper и попробуйте ещё раз.',
  upgradeRequiresSignIn:
    'Для обновления войдите в PhotoKeeper с учётной записью iCloud, привязанной к активной лицензии Personal.',
  purchaseRequiresSignIn:
    'Чтобы навсегда привязать учётную запись, начните покупку в PhotoKeeper после аутентификации учётной записи iCloud.',
  openingCheckout: 'Открывается безопасная оплата Paddle…',
  loadingTitle: 'Подготовка цен PhotoKeeper',
  loadingDescription: 'Загрузка безопасной конфигурации Sandbox и локальных итоговых сумм…',
  welcomeEyebrow: 'Оплата получена',
  welcomeTitle: 'Вернитесь в PhotoKeeper',
  welcomeDescription:
    'PhotoKeeper будет разблокирован или обновлён только после проверки службой лицензирования подписанного вебхука transaction.completed. Обычно это занимает несколько секунд.',
  welcomeClose: 'Теперь эту вкладку браузера можно безопасно закрыть.',
  fatalTitle: 'Оплата недоступна',
  invalidLink:
    'Эта ссылка на оплату недействительна. Вернитесь в PhotoKeeper и попробуйте ещё раз.',
  checkoutGone:
    'Эта оплата больше недоступна. Вернитесь в PhotoKeeper, чтобы безопасно начать или продолжить.',
  pricingUnavailable:
    'Цены временно недоступны. Вскоре вернитесь в PhotoKeeper и попробуйте ещё раз.',
  offers: {
    personal: {
      name: 'PhotoKeeper Personal — бессрочно',
      description:
        'Бессрочная лицензия PhotoKeeper с разовой оплатой. Навсегда привязывается к одной учётной записи iCloud.',
      features: [
        'Резервное копирование медиа из подтверждённой учётной записи «Фото iCloud»',
        'Локальное хранение на компьютере Windows',
        'Одна навсегда привязанная учётная запись iCloud',
        'Разовый платёж',
      ],
      cta: 'Купить Personal навсегда',
    },
    family: {
      name: 'PhotoKeeper Family — бессрочно',
      description:
        'Бессрочная лицензия PhotoKeeper с разовой оплатой. Навсегда привязывается максимум к трём учётным записям iCloud.',
      features: [
        'Резервное копирование медиа из подтверждённых учётных записей «Фото iCloud»',
        'Локальное хранение на компьютере Windows',
        'До трёх навсегда привязанных учётных записей iCloud',
        'Разовый платёж',
      ],
      cta: 'Купить Family навсегда',
    },
    upgrade: {
      name: 'Обновление PhotoKeeper с Personal до Family',
      description:
        'Преобразует активную бессрочную лицензию Personal в Family и увеличивает постоянный лимит учётных записей с одной до трёх. Не является самостоятельной лицензией.',
      features: [
        'Требуется активная бессрочная лицензия Personal',
        'Существующая лицензия и постоянная привязка сохраняются',
        'Лимит учётных записей увеличивается с одной до трёх',
        'Разовый платёж',
      ],
      cta: 'Обновить до Family',
    },
  },
};

const ar: CheckoutCopy = {
  metaTitle: 'تراخيص PhotoKeeper مدى الحياة',
  metaDescription: 'تراخيص PhotoKeeper مدى الحياة والدفع الآمن عبر Paddle.',
  languageLabel: 'اللغة',
  heroEyebrow: 'PhotoKeeper لنظام Windows',
  heroTitle: 'احتفظ بصورك بالقرب منك. ادفع مرة واحدة.',
  heroDescription:
    'انسخ الوسائط احتياطيًا من حساب صور iCloud الموثّق الخاص بك إلى مساحة تخزين تتحكم فيها.',
  sandboxBadge: 'دفع تجريبي في Sandbox · لا توجد دفعة حقيقية',
  offersLabel: 'تراخيص PhotoKeeper مدى الحياة',
  loadingPrices: 'جارٍ تحميل الأسعار المحلية لبلدك…',
  localizedPriceReady: 'يأتي الإجمالي المعروض مباشرةً من Paddle وفقًا لموقعك.',
  chooseInPhotoKeeper: 'اختر ترخيصًا داخل PhotoKeeper لإنشاء عملية دفع مرتبطة بشكل دائم.',
  priceUnavailable: 'السعر غير متاح',
  selectedBadge: 'تم اختياره في PhotoKeeper',
  oneTimeLicense: 'ترخيص مدى الحياة',
  licenseUpgrade: 'ترقية الترخيص',
  oneTimeQuantity: 'دفعة لمرة واحدة · الكمية 1',
  signInToUpgrade: 'سجّل الدخول للترقية',
  upgradeEyebrow: 'هل لديك Personal بالفعل؟',
  upgradeTitle: 'قم بترقية ترخيصك الحالي',
  upgradeDescription: 'تتحقق خدمة تراخيص PhotoKeeper من أهليتك للترقية قبل إنشاء المعاملة.',
  trustTitle: 'مصمم للتخزين المحلي أولًا',
  trustDescription:
    'يحفظ PhotoKeeper النسخ الاحتياطية في وحدة التخزين المحلية التي تختارها. تعالج Paddle الدفع فقط ولا تتلقى أبدًا بيانات اعتماد iCloud أو رموز المصادقة.',
  disclaimer:
    'تم تطوير PhotoKeeper بشكل مستقل، وهو غير تابع لشركة Apple Inc. ولا ترعاه أو تصادق عليه.',
  paymentCompleted: 'اكتملت عملية الدفع. يتحقق PhotoKeeper من ترخيصك بأمان.',
  checkoutError: 'تعذر إكمال الدفع. راجع بيانات الدفع وحاول مرة أخرى.',
  priceLoadError:
    'تعذر على Paddle تحميل الأسعار المحلية. تم تعطيل الدفع؛ عُد إلى PhotoKeeper وحاول مرة أخرى.',
  upgradeRequiresSignIn:
    'سجّل الدخول إلى PhotoKeeper باستخدام حساب iCloud المرتبط بترخيص Personal النشط للترقية.',
  purchaseRequiresSignIn:
    'لربط الحساب بشكل دائم، ابدأ الشراء من داخل PhotoKeeper بعد مصادقة حساب iCloud.',
  openingCheckout: 'جارٍ فتح الدفع الآمن عبر Paddle…',
  loadingTitle: 'جارٍ إعداد أسعار PhotoKeeper',
  loadingDescription: 'جارٍ تحميل إعداد Sandbox الآمن والإجماليات المحلية…',
  welcomeEyebrow: 'تم استلام الدفعة',
  welcomeTitle: 'عُد إلى PhotoKeeper',
  welcomeDescription:
    'لن يتم فتح PhotoKeeper أو ترقيته إلا بعد أن تتحقق خدمة التراخيص من webhook الموقّع transaction.completed. يستغرق ذلك عادةً بضع ثوانٍ.',
  welcomeClose: 'يمكنك الآن إغلاق علامة تبويب المتصفح هذه بأمان.',
  fatalTitle: 'الدفع غير متاح',
  invalidLink: 'رابط الدفع هذا غير صالح. عُد إلى PhotoKeeper وحاول مرة أخرى.',
  checkoutGone: 'لم تعد عملية الدفع هذه متاحة. عُد إلى PhotoKeeper للبدء أو المتابعة بأمان.',
  pricingUnavailable: 'الأسعار غير متاحة مؤقتًا. عُد إلى PhotoKeeper بعد قليل وحاول مرة أخرى.',
  offers: {
    personal: {
      name: 'PhotoKeeper Personal مدى الحياة',
      description: 'ترخيص PhotoKeeper مدى الحياة بدفعة واحدة. يرتبط بشكل دائم بحساب iCloud واحد.',
      features: [
        'نسخ الوسائط احتياطيًا من حساب صور iCloud الموثّق',
        'تخزين محلي على جهاز Windows',
        'حساب iCloud واحد مرتبط بشكل دائم',
        'دفعة لمرة واحدة',
      ],
      cta: 'شراء Personal مدى الحياة',
    },
    family: {
      name: 'PhotoKeeper Family مدى الحياة',
      description:
        'ترخيص PhotoKeeper مدى الحياة بدفعة واحدة. يرتبط بشكل دائم بما يصل إلى ثلاثة حسابات iCloud.',
      features: [
        'نسخ الوسائط احتياطيًا من حسابات صور iCloud الموثّقة',
        'تخزين محلي على جهاز Windows',
        'ما يصل إلى ثلاثة حسابات iCloud مرتبطة بشكل دائم',
        'دفعة لمرة واحدة',
      ],
      cta: 'شراء Family مدى الحياة',
    },
    upgrade: {
      name: 'ترقية PhotoKeeper من Personal إلى Family',
      description:
        'تحوّل ترخيص Personal النشط مدى الحياة إلى Family وتزيد الحد الدائم للحسابات من حساب واحد إلى ثلاثة. ليست ترخيصًا مستقلًا.',
      features: [
        'يتطلب ترخيص Personal نشطًا مدى الحياة',
        'يحافظ على الترخيص والربط الدائم الحاليين',
        'يرفع حد الحسابات من حساب واحد إلى ثلاثة',
        'دفعة لمرة واحدة',
      ],
      cta: 'الترقية إلى Family',
    },
  },
};

const catalogs: Record<SupportedLocale, CheckoutCopy> = {
  'en-US': enUS,
  'en-GB': enGB,
  'zh-CN': zhCN,
  'zh-TW': zhTW,
  'ja-JP': jaJP,
  'ko-KR': koKR,
  'es-ES': esES,
  'pt-PT': ptPT,
  'pt-BR': ptBR,
  'fr-FR': frFR,
  'de-DE': deDE,
  'it-IT': itIT,
  'nl-NL': nlNL,
  'pl-PL': plPL,
  'da-DK': daDK,
  'sv-SE': svSE,
  'nb-NO': nbNO,
  'tr-TR': trTR,
  'ru-RU': ruRU,
  ar,
};

type PlatformNeutralOfferCopy = {
  heroEyebrow: string;
  storageFeature: string;
  onlineRefreshFeature: string;
};

const platformNeutralOfferCopy: Record<SupportedLocale, PlatformNeutralOfferCopy> = {
  'en-US': {
    heroEyebrow: 'PhotoKeeper desktop app',
    storageFeature: 'Local-first storage on your computer',
    onlineRefreshFeature: 'Offline access: up to 24 hours in the first 15 days or during a refund; otherwise up to 30 days',
  },
  'en-GB': {
    heroEyebrow: 'PhotoKeeper desktop app',
    storageFeature: 'Local-first storage on your computer',
    onlineRefreshFeature: 'Offline access: up to 24 hours in the first 15 days or during a refund; otherwise up to 30 days',
  },
  'zh-CN': {
    heroEyebrow: 'PhotoKeeper 桌面应用',
    storageFeature: '在您的电脑上采用本地优先存储',
    onlineRefreshFeature: '购买或升级后 15 天内、退款期间可离线 24 小时；其他情况最长 30 天',
  },
  'zh-TW': {
    heroEyebrow: 'PhotoKeeper 桌面應用程式',
    storageFeature: '在您的電腦上採用本機優先儲存',
    onlineRefreshFeature: '每次授權重新整理之間最長可離線使用 30 天',
  },
  'ja-JP': {
    heroEyebrow: 'PhotoKeeper デスクトップアプリ',
    storageFeature: 'お使いのコンピューター上のローカルファーストストレージ',
    onlineRefreshFeature: 'ライセンスの更新間隔は最大 30 日間オフラインで使用可能',
  },
  'ko-KR': {
    heroEyebrow: 'PhotoKeeper 데스크톱 앱',
    storageFeature: '컴퓨터의 로컬 우선 저장소',
    onlineRefreshFeature: '라이선스 갱신 사이에 최대 30일 동안 오프라인 사용 가능',
  },
  'es-ES': {
    heroEyebrow: 'Aplicación de escritorio PhotoKeeper',
    storageFeature: 'Almacenamiento local en tu ordenador',
    onlineRefreshFeature:
      'Funciona sin conexión hasta 30 días entre actualizaciones de la licencia',
  },
  'pt-PT': {
    heroEyebrow: 'Aplicação PhotoKeeper para computador',
    storageFeature: 'Armazenamento local no seu computador',
    onlineRefreshFeature: 'Funciona offline até 30 dias entre atualizações da licença',
  },
  'pt-BR': {
    heroEyebrow: 'Aplicativo PhotoKeeper para desktop',
    storageFeature: 'Armazenamento local no seu computador',
    onlineRefreshFeature: 'Funciona offline por até 30 dias entre atualizações da licença',
  },
  'fr-FR': {
    heroEyebrow: 'Application de bureau PhotoKeeper',
    storageFeature: 'Stockage local sur votre ordinateur',
    onlineRefreshFeature:
      'Fonctionne hors ligne jusqu’à 30 jours entre les actualisations de licence',
  },
  'de-DE': {
    heroEyebrow: 'PhotoKeeper Desktop-App',
    storageFeature: 'Lokaler Speicher auf Ihrem Computer',
    onlineRefreshFeature: 'Bis zu 30 Tage offline zwischen Lizenzaktualisierungen nutzbar',
  },
  'it-IT': {
    heroEyebrow: 'App desktop PhotoKeeper',
    storageFeature: 'Archiviazione locale sul tuo computer',
    onlineRefreshFeature: 'Funziona offline fino a 30 giorni tra gli aggiornamenti della licenza',
  },
  'nl-NL': {
    heroEyebrow: 'PhotoKeeper-desktopapp',
    storageFeature: 'Lokale opslag op je computer',
    onlineRefreshFeature: 'Werkt maximaal 30 dagen offline tussen licentievernieuwingen',
  },
  'pl-PL': {
    heroEyebrow: 'Aplikacja komputerowa PhotoKeeper',
    storageFeature: 'Lokalne przechowywanie na komputerze',
    onlineRefreshFeature: 'Działa offline do 30 dni między odświeżeniami licencji',
  },
  'da-DK': {
    heroEyebrow: 'PhotoKeeper-computerapp',
    storageFeature: 'Lokal lagring på din computer',
    onlineRefreshFeature: 'Kan bruges offline i op til 30 dage mellem licensopdateringer',
  },
  'sv-SE': {
    heroEyebrow: 'PhotoKeeper-skrivbordsapp',
    storageFeature: 'Lokal lagring på din dator',
    onlineRefreshFeature: 'Fungerar offline i upp till 30 dagar mellan licensuppdateringar',
  },
  'nb-NO': {
    heroEyebrow: 'PhotoKeeper-skrivebordsapp',
    storageFeature: 'Lokal lagring på datamaskinen din',
    onlineRefreshFeature: 'Fungerer frakoblet i opptil 30 dager mellom lisensoppdateringer',
  },
  'tr-TR': {
    heroEyebrow: 'PhotoKeeper masaüstü uygulaması',
    storageFeature: 'Bilgisayarınızda yerel öncelikli depolama',
    onlineRefreshFeature: 'Lisans yenilemeleri arasında 30 güne kadar çevrimdışı çalışır',
  },
  'ru-RU': {
    heroEyebrow: 'Приложение PhotoKeeper для компьютера',
    storageFeature: 'Локальное хранение на вашем компьютере',
    onlineRefreshFeature: 'Работает без подключения до 30 дней между обновлениями лицензии',
  },
  ar: {
    heroEyebrow: 'تطبيق PhotoKeeper لسطح المكتب',
    storageFeature: 'تخزين محلي على جهاز الكمبيوتر الخاص بك',
    onlineRefreshFeature: 'يعمل دون اتصال لمدة تصل إلى 30 يومًا بين تحديثات الترخيص',
  },
};

const platformNeutralCatalogs = Object.fromEntries(
  Object.entries(catalogs).map(([locale, copy]) => [
    locale,
    platformNeutralCopy(copy, platformNeutralOfferCopy[locale as SupportedLocale]),
  ]),
) as Record<SupportedLocale, CheckoutCopy>;

function platformNeutralCopy(
  copy: CheckoutCopy,
  localized: PlatformNeutralOfferCopy,
): CheckoutCopy {
  const features = (offer: OfferCopy, includeStorage: boolean): readonly string[] =>
    offer.features.map((feature, index) => {
      if (includeStorage && index === 1) return localized.storageFeature;
      if (index === 3) return localized.onlineRefreshFeature;
      return feature;
    });

  return {
    ...copy,
    heroEyebrow: localized.heroEyebrow,
    offers: {
      ...copy.offers,
      personal: {
        ...copy.offers.personal,
        features: features(copy.offers.personal, true),
      },
      family: {
        ...copy.offers.family,
        features: features(copy.offers.family, true),
      },
      upgrade: {
        ...copy.offers.upgrade,
        features: features(copy.offers.upgrade, false),
      },
    },
  };
}

const localeOptionByCode = new Map<SupportedLocale, (typeof localeOptions)[number]>(
  localeOptions.map((option) => [option.code, option]),
);

const exactAliases: Readonly<Record<string, SupportedLocale>> = {
  en: 'en-US',
  'en-us': 'en-US',
  'en-gb': 'en-GB',
  'en-ie': 'en-GB',
  'en-au': 'en-GB',
  'en-nz': 'en-GB',
  'zh-hans': 'zh-CN',
  'zh-cn': 'zh-CN',
  'zh-sg': 'zh-CN',
  'zh-hant': 'zh-TW',
  'zh-tw': 'zh-TW',
  'zh-hk': 'zh-TW',
  'zh-mo': 'zh-TW',
  ja: 'ja-JP',
  'ja-jp': 'ja-JP',
  ko: 'ko-KR',
  'ko-kr': 'ko-KR',
  es: 'es-ES',
  'es-es': 'es-ES',
  pt: 'pt-PT',
  'pt-pt': 'pt-PT',
  'pt-br': 'pt-BR',
  fr: 'fr-FR',
  'fr-fr': 'fr-FR',
  de: 'de-DE',
  'de-de': 'de-DE',
  it: 'it-IT',
  'it-it': 'it-IT',
  nl: 'nl-NL',
  'nl-nl': 'nl-NL',
  pl: 'pl-PL',
  'pl-pl': 'pl-PL',
  da: 'da-DK',
  'da-dk': 'da-DK',
  sv: 'sv-SE',
  'sv-se': 'sv-SE',
  no: 'nb-NO',
  nb: 'nb-NO',
  nn: 'nb-NO',
  'nb-no': 'nb-NO',
  tr: 'tr-TR',
  'tr-tr': 'tr-TR',
  ru: 'ru-RU',
  'ru-ru': 'ru-RU',
  ar: 'ar',
};

const localeByLanguage: Readonly<Record<string, SupportedLocale>> = {
  en: 'en-US',
  ja: 'ja-JP',
  ko: 'ko-KR',
  es: 'es-ES',
  pt: 'pt-PT',
  fr: 'fr-FR',
  de: 'de-DE',
  it: 'it-IT',
  nl: 'nl-NL',
  pl: 'pl-PL',
  da: 'da-DK',
  sv: 'sv-SE',
  no: 'nb-NO',
  nb: 'nb-NO',
  nn: 'nb-NO',
  tr: 'tr-TR',
  ru: 'ru-RU',
  ar: 'ar',
};

export function matchSupportedLocale(
  value: string | null | undefined,
): SupportedLocale | undefined {
  if (!value) return undefined;
  const normalized = value.trim().replaceAll('_', '-').toLowerCase();
  if (!normalized) return undefined;
  const exact = exactAliases[normalized];
  if (exact) return exact;

  const [language] = normalized.split('-');
  if (language === 'zh') {
    return normalized.includes('hant') || /^zh-(tw|hk|mo)(-|$)/.test(normalized)
      ? 'zh-TW'
      : 'zh-CN';
  }
  if (language === 'pt' && /^pt-br(-|$)/.test(normalized)) return 'pt-BR';
  return language ? localeByLanguage[language] : undefined;
}

export function resolveLocale(
  search: string,
  storedLocale: string | null | undefined,
  browserLanguages: readonly string[],
): SupportedLocale {
  let requestedLocale: string | null = null;
  try {
    requestedLocale = new URLSearchParams(search).get('lang');
  } catch {
    requestedLocale = null;
  }
  for (const candidate of [requestedLocale, storedLocale, ...browserLanguages]) {
    const locale = matchSupportedLocale(candidate);
    if (locale && reviewedLocaleSet.has(locale)) return locale;
  }
  return 'en-US';
}

export function copyFor(locale: SupportedLocale): CheckoutCopy {
  return platformNeutralCatalogs[locale];
}

export function paddleLocaleFor(locale: SupportedLocale): PaddleCheckoutLocale {
  return localeOptionByCode.get(locale)?.paddleLocale ?? 'en';
}

export function directionFor(locale: SupportedLocale): 'ltr' | 'rtl' {
  return localeOptionByCode.get(locale)?.direction ?? 'ltr';
}

export function urlWithLocale(input: string | URL, locale: SupportedLocale): URL {
  const url = new URL(input.toString());
  url.searchParams.set('lang', locale);
  return url;
}
